import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Container from "../components/Container";
import faroLogo from "../assets/faro.png";
import romanoImg from "../assets/romano_picture.png";
import rivettiImg from "../assets/rivetti_picture.jpg";

/* ─── Palette ─── */
const C = {
  navy: "#0B1F3A",
  navyLight: "#112B4D",
  navyMid: "#0E2545",
  amber: "#FFC857",
  green: "#22C55E",
  amberDark: "#E6B04E",
  amberGlow: "rgba(255, 200, 87, 0.15)",
  paper: "#F5F2EA",
  white: "#F5F7FA",
  gray: "#6B7280",
  grayLight: "#9CA3AF",
  grayDark: "#4B5563",
};

const SERIF = "'Fraunces', 'Playfair Display', Georgia, serif";
const SANS = "'Inter', 'DM Sans', system-ui, sans-serif";
const MONO = "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, monospace";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" },
  }),
};

/* ─── FARO Logo ─── */
function FaroLogo({ className = "h-8 w-auto" }) {
  return <img src={faroLogo} alt="FARO" className={className} draggable={false} />;
}

/* ─── Faro Watermark ─── */
function FaroWatermark({ className = "", position = "left" }) {
  const isRight = position === "right";
  return (
    <div className={`absolute pointer-events-none select-none ${className}`}
      style={{ transform: isRight ? "scaleX(-1)" : "none" }}>
      <img src={faroLogo} alt="" className="w-full h-full object-contain"
        style={{ opacity: 0.04, filter: "brightness(2)" }} draggable={false} />
    </div>
  );
}

/* ─── Amber Sidebar Accent ─── */
function AmberSidebar() {
  return (
    <div className="absolute top-0 right-0 w-2 md:w-3 h-full pointer-events-none"
      style={{ background: C.amber, opacity: 0.15 }} />
  );
}

/* ─── Amber rule ─── */
function AmberRule({ width = "52px", className = "" }) {
  return (
    <div className={`h-0.5 rounded-full ${className}`} style={{ width, background: C.amber }} />
  );
}

/* ─── Animated circular stat ─── */
/* Icons for the three stat circles — flat, navy/amber/white like the benefit illustrations */
const STAT_ICONS = {
  orders: (
    <>
      <rect x="8" y="8" width="34" height="46" rx="3" fill="rgba(245,247,250,0.08)" stroke={C.white} strokeWidth="3" />
      <rect x="17" y="4" width="16" height="8" rx="2" fill={C.white} />
      <path d="M15 24l3 3 5-6M15 36l3 3 5-6" fill="none" stroke={C.amber} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M28 25h8M28 37h8" stroke={C.white} strokeWidth="3" strokeLinecap="round" />
      <circle cx="47" cy="47" r="13" fill={C.amber} />
      <path d="M47 40v7l5 3" fill="none" stroke={C.navy} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  forecast: (
    <>
      <circle cx="28" cy="36" r="22" fill="rgba(245,247,250,0.08)" stroke={C.white} strokeWidth="3" />
      <circle cx="28" cy="36" r="14" fill="none" stroke={C.white} strokeWidth="3" />
      <circle cx="28" cy="36" r="6.5" fill={C.amber} />
      <path d="M31 33L55 9" stroke={C.amber} strokeWidth="3.5" strokeLinecap="round" />
      <path d="M49 6v9h9" fill="none" stroke={C.amber} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  radar: (
    <>
      <circle cx="32" cy="32" r="26" fill="rgba(245,247,250,0.08)" stroke={C.white} strokeWidth="3" />
      <circle cx="32" cy="32" r="16" fill="none" stroke="rgba(245,247,250,0.45)" strokeWidth="2.5" />
      <path d="M32 32 L32 6 A26 26 0 0 1 54.5 19 Z" fill={C.amber} opacity=".35" />
      <path d="M32 32 L54.5 19" stroke={C.amber} strokeWidth="3.5" strokeLinecap="round" />
      <circle cx="32" cy="32" r="3.5" fill={C.white} />
      <circle cx="42" cy="15" r="3.5" fill={C.amber} />
      <circle cx="20" cy="44" r="3" fill={C.amber} />
      <circle cx="45" cy="42" r="2.5" fill={C.white} />
    </>
  ),
  losses: (
    <>
      <path d="M6 8 C 22 9, 36 26, 50 30 L 50 32 L 6 32 Z" fill={C.amber} opacity=".45" />
      <path d="M6 56 C 22 55, 36 38, 50 34 L 50 32 L 6 32 Z" fill={C.grayLight} opacity=".45" />
      <path d="M6 8 C 22 9, 36 26, 50 30" fill="none" stroke={C.amber} strokeWidth="3" strokeLinecap="round" />
      <path d="M6 56 C 22 55, 36 38, 50 34" fill="none" stroke={C.grayLight} strokeWidth="3" strokeLinecap="round" />
      <path d="M6 32 H50" stroke={C.white} strokeWidth="3" strokeLinecap="round" />
      <circle cx="52" cy="32" r="6" fill={C.amber} stroke={C.navyMid} strokeWidth="2" />
    </>
  ),
};

function CircularStat({ icon, label, index, size = 200 }) {
  const SIZE = size, STROKE = size / 20, R = (SIZE - STROKE) / 2 - 2;
  const CIRC = 2 * Math.PI * R;
  return (
    <motion.div custom={index} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
      className="flex flex-col items-center text-center">
      <div className="relative mb-4" style={{ width: SIZE, height: SIZE }}>
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
          <circle cx={SIZE / 2} cy={SIZE / 2} r={R}
            fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={STROKE} />
          <motion.circle
            cx={SIZE / 2} cy={SIZE / 2} r={R}
            fill="none" stroke={C.amber} strokeWidth={STROKE} strokeLinecap="round"
            strokeDasharray={CIRC}
            initial={{ strokeDashoffset: CIRC }}
            whileInView={{ strokeDashoffset: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, delay: 0.15 + index * 0.12, ease: [0.22, 0.61, 0.36, 1] }}
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            shapeRendering="geometricPrecision"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <svg viewBox="0 0 64 64" className="w-[54%] h-auto" aria-hidden="true">
            {STAT_ICONS[icon]}
          </svg>
        </div>
      </div>
      <span className="text-[12.5px] md:text-[13.5px] font-bold uppercase tracking-[0.18em] max-w-[200px]"
        style={{ color: C.white, fontFamily: SANS }}>
        {label}
      </span>
    </motion.div>
  );
}



/* ─── Section label used inside platform mock ─── */
function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-2 mt-0.5">
      <span className="w-1 h-3 rounded-sm" style={{ background: "#FFC857" }} />
      <span className="text-[8px] font-bold uppercase tracking-[0.2em]" style={{ color: "#FFC857", fontFamily: "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, monospace" }}>
        {children}
      </span>
      <span className="flex-1 h-px" style={{ background: "rgba(255,200,87,0.18)" }} />
    </div>
  );
}

/* ─── Mock card + header shared by the Radar / losses views ─── */
const mockCard = (accent) => ({
  background: "rgba(255,255,255,0.04)",
  border: `1px solid ${accent ? "rgba(255,200,87,0.5)" : "rgba(255,200,87,0.18)"}`,
});
function MockHeader({ title, kicker }) {
  return (
    <div className="flex flex-col">
      <span className="text-[12px] font-bold leading-tight" style={{ color: C.white, fontFamily: SANS }}>{title}</span>
      <span className="text-[7.5px] uppercase tracking-wider mt-0.5" style={{ color: C.amber, fontFamily: MONO }}>✦ {kicker}</span>
    </div>
  );
}
const mockEnter = {
  initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -6 },
  transition: { duration: 0.35 },
};
const rise = (i) => ({
  initial: { opacity: 0, y: 4 }, animate: { opacity: 1, y: 0 },
  transition: { delay: 0.08 + i * 0.06, duration: 0.3 },
});

/* Radar — today's tracking of every order plus the insights that need action (platform: RadarTab) */
function RadarMockView({ en }) {
  const counters = [
    { l: en ? "Created today" : "Generadas hoy", v: "12" },
    { l: en ? "On the way" : "En camino", v: "18", sub: en ? "5 arrive today" : "5 llegan hoy", accent: true },
    { l: en ? "Late" : "Atrasadas", v: "2", late: true },
  ];
  const order = en ? "Order" : "Pedir";
  const groups = [
    { t: "Stock", rows: [
      { g: en ? "Out of stock" : "Sin stock", p: "Coca-Cola 2,25 L", a: order, c: "#f87171" },
      { g: en ? "Out of stock" : "Sin stock", p: en ? "Sunflower oil 1.5 L" : "Aceite girasol 1,5 L", a: order, c: "#f87171" },
      { g: en ? "At risk" : "En riesgo", p: en ? "Whole milk 1 L" : "Leche entera 1 L", a: order, c: C.amber },
      { g: en ? "Overstock" : "Sobrestock", p: en ? "Yerba 1 kg · $ 180,000" : "Yerba 1 kg · $ 180.000", a: en ? "Configure" : "Configurar", c: C.amber },
    ] },
    { t: en ? "Orders" : "Órdenes", rows: [
      { g: en ? "Not reviewed" : "Sin revisar", p: en ? "Order of 10/10 · Store 04" : "Orden del 10/10 · Tienda 04", a: en ? "Review" : "Revisar", c: C.grayLight },
    ] },
    { t: en ? "Sales and forecasts" : "Ventas y predicciones", rows: [
      { g: en ? "Unusual sales" : "Venta inusual", p: en ? "Charcoal 4 kg · 3.4× the mean" : "Carbón 4 kg · 3,4× la media", a: en ? "Forecast" : "Predicción", c: "#4ade80" },
      { g: en ? "Unusual sales" : "Venta inusual", p: en ? "Ice 2 kg · 5× the mean" : "Hielo 2 kg · 5× la media", a: en ? "Forecast" : "Predicción", c: "#4ade80" },
    ] },
  ];
  return (
    <motion.div key="radar" {...mockEnter} className="flex-1 flex flex-col gap-2 min-h-0">
      <MockHeader title="Radar" kicker={en ? "What needs attention today" : "Lo que necesita atención hoy"} />
      <SectionLabel>{en ? "Order tracking" : "Seguimiento de pedidos"}</SectionLabel>
      <div className="grid grid-cols-3 gap-1.5">
        {counters.map((k, i) => (
          <motion.div key={k.l} {...rise(i)} className="rounded-md p-1.5 text-center" style={mockCard(k.accent)}>
            <span className="block text-[7px] uppercase tracking-wider leading-none mb-1" style={{ color: "rgba(245,247,250,0.5)", fontFamily: MONO }}>{k.l}</span>
            <span className="block text-[13px] font-bold leading-none" style={{ color: k.late ? "#f87171" : k.accent ? C.amber : C.white, fontFamily: SANS }}>{k.v}</span>
            {k.sub && <span className="block text-[7px] mt-1" style={{ color: "#4ade80", fontFamily: MONO }}>{k.sub}</span>}
          </motion.div>
        ))}
      </div>
      <SectionLabel>Insights</SectionLabel>
      <div className="flex-1 flex flex-col gap-1.5 min-h-0">
        {groups.map((grp, gi) => (
          <div key={grp.t} className="flex flex-col gap-1">
            <span className="text-[8px] font-bold uppercase tracking-[0.16em]" style={{ color: "rgba(245,247,250,0.6)", fontFamily: MONO }}>{grp.t}</span>
            {grp.rows.map((r, i) => (
              <motion.div key={r.p} {...rise(3 + gi * 2 + i)} className="rounded-md px-2 py-1 flex items-center gap-2" style={mockCard(false)}>
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: r.c, boxShadow: `0 0 6px ${r.c}` }} />
                <span className="w-[62px] flex-shrink-0 text-[7.5px] font-bold uppercase tracking-wider" style={{ color: r.c, fontFamily: MONO }}>{r.g}</span>
                <span className="flex-1 min-w-0 truncate text-[9px] font-semibold" style={{ color: C.white, fontFamily: SANS }}>{r.p}</span>
                <span className="text-[7.5px] font-bold px-2 py-0.5 rounded-full" style={{ background: C.amber, color: C.navy, fontFamily: SANS }}>{r.a}</span>
              </motion.div>
            ))}
          </div>
        ))}
      </div>
    </motion.div>
  );
}

/* Quiebres y sobrestock — what the shelf costs on each side (platform: LossesTab + General) */
function LossesMockView({ en }) {
  const cards = [
    { l: en ? "Stockouts · sales not made" : "Quiebres · ventas que no se hicieron", v: "$ 1,2 M", d: "−38%", c: C.grayLight },
    { l: en ? "Overstock · money tied up" : "Sobrestock · dinero parado", v: "$ 3,4 M", d: "−21%", c: C.amber },
  ];
  const where = [
    { p: en ? "Soft drinks" : "Gaseosas", w: 0.82 },
    { p: en ? "Dairy" : "Lácteos", w: 0.61 },
    { p: en ? "Bakery" : "Panificados", w: 0.44 },
  ];
  return (
    <motion.div key="losses" {...mockEnter} className="flex-1 flex flex-col gap-2 min-h-0">
      <MockHeader title={en ? "Stockouts & overstock" : "Quiebres y sobrestock"} kicker={en ? "What stock costs you" : "Lo que te cuesta el stock"} />
      <div className="grid grid-cols-2 gap-1.5">
        {cards.map((k, i) => (
          <motion.div key={k.l} {...rise(i)} className="rounded-md p-2" style={mockCard(i === 1)}>
            <span className="block text-[7px] uppercase tracking-wider leading-tight mb-1" style={{ color: "rgba(245,247,250,0.5)", fontFamily: MONO }}>{k.l}</span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-[14px] font-bold leading-none" style={{ color: k.c, fontFamily: SANS }}>{k.v}</span>
              <span className="text-[7.5px] font-bold" style={{ color: "#4ade80" }}>↓ {k.d}</span>
            </div>
          </motion.div>
        ))}
      </div>
      <SectionLabel>{en ? "Over time" : "En el tiempo"}</SectionLabel>
      <motion.div {...rise(2)} className="rounded-md p-2 flex-1 min-h-0 flex flex-col" style={mockCard(false)}>
        {/* Same stock chart as the rest of the site, mid-way: both bands still open but narrowing towards the optimal stock */}
        <svg viewBox="0 0 500 210" className="w-full flex-1 min-h-0">
          <line x1="34" y1="20" x2="34" y2="178" stroke="rgba(245,247,250,0.5)" strokeWidth="1.5" />
          <line x1="34" y1="178" x2="384" y2="178" stroke="rgba(245,247,250,0.5)" strokeWidth="1.5" />
          <path d="M34 12 l-5 9 h10 z" fill="rgba(245,247,250,0.5)" />
          <path d="M392 178 l-9 -5 v10 z" fill="rgba(245,247,250,0.5)" />
          <text x="44" y="18" style={{ fontSize: 13, fontWeight: 700, fill: C.white, fontFamily: SANS }}>Stock</text>
          <text x="210" y="200" style={{ fontSize: 13, fontWeight: 700, fill: C.white, textAnchor: "middle", fontFamily: SANS }}>{en ? "Time with RUMBO" : "Tiempo con RUMBO"}</text>
          <g transform="translate(24 28)">
            <path d="M10 10 C 110 14, 220 52, 320 60 L 320 75 L 10 75 Z" fill={C.amber} opacity=".35" />
            <path d="M10 140 C 110 136, 220 98, 320 90 L 320 75 L 10 75 Z" fill={C.grayLight} opacity=".3" />
            <path d="M10 10 C 110 14, 220 52, 320 60" fill="none" stroke={C.amber} strokeWidth="2.5" />
            <path d="M10 140 C 110 136, 220 98, 320 90" fill="none" stroke={C.grayLight} strokeWidth="2.5" />
            <line x1="10" y1="75" x2="330" y2="75" stroke={C.white} strokeWidth="2" />
            <text x="22" y="52" style={{ fontSize: 14, fontWeight: 700, fill: C.white, fontFamily: SANS }}>{en ? "Overstock" : "Sobrestock"}</text>
            <text x="22" y="104" style={{ fontSize: 14, fontWeight: 700, fill: C.white, fontFamily: SANS }}>{en ? "Stockouts" : "Quiebres de stock"}</text>
            <line x1="320" y1="60" x2="320" y2="150" stroke="rgba(245,247,250,0.6)" strokeWidth="1.5" strokeDasharray="4 4" />
            <rect x="348" y="61" width="118" height="28" rx="14" fill={C.amber} />
            <text x="407" y="80" style={{ fontSize: 13, fontWeight: 700, fill: C.navy, textAnchor: "middle", fontFamily: SANS }}>{en ? "Optimal stock" : "Stock óptimo"}</text>
          </g>
        </svg>
      </motion.div>
      <SectionLabel>{en ? "Where it is lost" : "Dónde se pierde"}</SectionLabel>
      <div className="flex flex-col gap-1">
        {where.map((r, i) => (
          <motion.div key={r.p} {...rise(i + 3)} className="flex items-center gap-2">
            <span className="w-16 text-[8.5px] font-semibold truncate" style={{ color: C.white, fontFamily: SANS }}>{r.p}</span>
            <span className="flex-1 h-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }}>
              <motion.span className="block h-full rounded-full" style={{ background: C.amber }}
                initial={{ width: 0 }} animate={{ width: `${r.w * 100}%` }} transition={{ delay: 0.3 + i * 0.08, duration: 0.6 }} />
            </span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

/* ─── Animated platform mock — driven by parent (key features accordion) ─── */
/* General — the overall picture of the stock management (platform: GeneralTab) */
function GeneralMockView({ en }) {
  const orders = [
    { l: en ? "Orders" : "Órdenes", v: "248" },
    { l: en ? "Products" : "Productos", v: "1.320" },
    { l: en ? "Units" : "Unidades", v: "38,6 k" },
  ];
  const review = [
    { l: en ? "Accepted" : "Aceptadas", v: 68, c: "#4ade80" },
    { l: en ? "Adjusted" : "Modificadas", v: 17, c: C.amber },
    { l: en ? "Declined" : "Rechazadas", v: 6, c: "#f87171" },
    { l: en ? "Pending" : "Pendientes", v: 9, c: "rgba(245,247,250,0.35)" },
  ];
  const label = { color: "rgba(245,247,250,0.5)", fontFamily: MONO };
  let acc = 0;
  return (
    <motion.div key="general" {...mockEnter} className="flex-1 flex flex-col gap-2 min-h-0">
      <MockHeader title="General" kicker={en ? "Your stock management at a glance" : "Tu gestión de stock de un vistazo"} />

      <SectionLabel>{en ? "RUMBO impact" : "Impacto de RUMBO"}</SectionLabel>
      <motion.div {...rise(0)} className="rounded-md p-3 flex-1 min-h-0 flex flex-col gap-2" style={mockCard(true)}>
        <div className="grid grid-cols-[1.2fr_1fr_1fr] gap-3 items-end">
          <div>
            <span className="block text-[7.5px] uppercase tracking-wider mb-1" style={label}>{en ? "Monthly loss" : "Pérdida del mes"}</span>
            <div className="flex items-baseline gap-1.5">
              <span className="block text-[22px] font-bold leading-none" style={{ color: C.amber, fontFamily: SANS }}>$ 1,8 M</span>
              <span className="text-[8px] font-bold" style={{ color: "#4ade80" }}>↓ −62%</span>
            </div>
          </div>
          {[
            { l: en ? "Stockouts" : "Quiebres", v: "$ 0,4 M", d: "−67%", c: C.grayLight },
            { l: en ? "Overstock" : "Sobrestock", v: "$ 1,4 M", d: "−60%", c: C.amber },
          ].map((k) => (
            <div key={k.l} className="pl-3" style={{ borderLeft: "1px solid rgba(255,255,255,0.08)" }}>
              <span className="block text-[7.5px] uppercase tracking-wider mb-1" style={label}>{k.l}</span>
              <div className="flex items-baseline gap-1">
                <span className="block text-[14px] font-bold leading-none" style={{ color: k.c, fontFamily: SANS }}>{k.v}</span>
                <span className="text-[7.5px] font-bold" style={{ color: "#4ade80" }}>↓ {k.d}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="flex-1 min-h-0 flex items-end gap-2 pt-1">
          {[[0.82, 0.30], [0.70, 0.26], [0.58, 0.21], [0.47, 0.17], [0.38, 0.13], [0.31, 0.10]].map(([over, out], i) => (
            <div key={i} className="flex-1 h-full flex flex-col justify-end items-center gap-1">
              <div className="w-full flex flex-col justify-end" style={{ height: "100%" }}>
                <motion.div className="w-full rounded-t-sm" style={{ background: C.amber }}
                  initial={{ height: 0 }} animate={{ height: `${over * 70}%` }} transition={{ delay: 0.2 + i * 0.06, duration: 0.5 }} />
                <motion.div className="w-full" style={{ background: C.grayLight }}
                  initial={{ height: 0 }} animate={{ height: `${out * 70}%` }} transition={{ delay: 0.2 + i * 0.06, duration: 0.5 }} />
              </div>
              <span className="text-[7px]" style={label}>{(en ? ["May", "Jun", "Jul", "Aug", "Sep", "Oct"] : ["May", "Jun", "Jul", "Ago", "Sep", "Oct"])[i]}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <span className="text-[7.5px]" style={{ color: C.amber, fontFamily: MONO }}>● {en ? "Overstock" : "Sobrestock"}</span>
          <span className="text-[7.5px]" style={{ color: C.grayLight, fontFamily: MONO }}>● {en ? "Stockouts" : "Quiebres"}</span>
        </div>
      </motion.div>

      <SectionLabel>{en ? "Orders this period" : "Órdenes del período"}</SectionLabel>
      <div className="grid grid-cols-[1fr_1fr_1fr_1.5fr] gap-1.5">
        {orders.map((k, i) => (
          <motion.div key={k.l} {...rise(i + 1)} className="rounded-md p-2.5 flex flex-col justify-center text-center" style={mockCard(false)}>
            <span className="block text-[7.5px] uppercase tracking-wider leading-none mb-1.5" style={label}>{k.l}</span>
            <span className="block text-[16px] font-bold leading-none" style={{ color: C.white, fontFamily: SANS }}>{k.v}</span>
          </motion.div>
        ))}
        <motion.div {...rise(4)} className="rounded-md p-2 flex items-center gap-2" style={mockCard(false)}>
          <svg viewBox="0 0 36 36" className="w-12 h-12 flex-shrink-0">
            {review.map((r) => {
              const el = (
                <circle key={r.l} cx="18" cy="18" r="15.9155" fill="none" stroke={r.c} strokeWidth="4"
                  strokeDasharray={`${r.v} ${100 - r.v}`} strokeDashoffset={25 - acc} />
              );
              acc += r.v;
              return el;
            })}
          </svg>
          <div className="flex flex-col gap-1 min-w-0">
            {review.map((r) => (
              <span key={r.l} className="text-[7.5px] leading-none truncate" style={{ color: "rgba(245,247,250,0.75)", fontFamily: MONO }}>
                <span style={{ color: r.c }}>●</span> {r.l} {r.v}%
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export function PlatformMock({ en, view = "replenishment", clickPredictionLink = false }) {
  // view: "replenishment" | "automation" | "predictions" | "radar" | "losses" | "general"
  const activeTab = view === "automation" ? "replenishment" : view;

  return (
    <div className="relative aspect-[5/4] w-full rounded-2xl overflow-hidden flex"
      style={{
        background: `linear-gradient(135deg, ${C.navyLight} 0%, ${C.navyMid} 100%)`,
        border: `1px solid rgba(255,200,87,0.25)`,
        boxShadow: `0 20px 60px rgba(0,0,0,0.35), 0 0 80px rgba(255,200,87,0.08)`,
      }}>
      {/* SIDEBAR */}
      <div className="flex flex-col gap-1.5 py-4 px-2.5 flex-shrink-0 w-[28%] md:w-[26%]"
        style={{ background: "rgba(0,0,0,0.18)", borderRight: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="mb-3 ml-1 flex items-center gap-1.5">
          <FaroLogo className="h-5 w-auto object-contain" />
          <span className="text-[11px] font-bold tracking-wide" style={{ color: C.white, fontFamily: SANS }}>FARO</span>
        </div>
        {/* platform-wide tabs, outside any product */}
        <Tab label="General" active={false} />
        <Tab label={en ? "Sales" : "Ventas"} active={false} />
        <Tab label="Stock" active={false} />
        {/* RUMBO product group */}
        <div className="mt-3 ml-0.5 pl-1.5 flex flex-col gap-1" style={{ borderLeft: `2px solid ${C.amber}` }}>
          <span className="text-[8px] font-bold uppercase tracking-[0.2em] mb-0.5 ml-1" style={{ color: C.amber, fontFamily: MONO }}>RUMBO</span>
          <Tab label="General" active={activeTab === "general"} />
          <Tab label={en ? "Orders" : "Órdenes"} active={activeTab === "replenishment"} />
          <Tab label={en ? "Predictions" : "Predicciones"} active={activeTab === "predictions"} />
          <Tab label="Radar" active={activeTab === "radar"} />
          <Tab label={en ? "Stockouts & overstock" : "Quiebres y sobrestock"} active={activeTab === "losses"} />
        </div>
      </div>

      {/* CONTENT */}
      <div className="flex-1 p-4 md:p-5 flex flex-col min-w-0 relative">
        {/* top bar */}
        <div className="flex items-center justify-between mb-3">
          <span className="w-20 h-2 rounded-full" style={{ background: "rgba(255,255,255,0.18)" }} />
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: C.amber, boxShadow: `0 0 6px ${C.amber}` }} />
            <span className="w-10 h-2 rounded-full" style={{ background: "rgba(255,255,255,0.15)" }} />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {view === "radar" ? (
            <RadarMockView en={en} />
          ) : view === "losses" ? (
            <LossesMockView en={en} />
          ) : view === "general" ? (
            <GeneralMockView en={en} />
          ) : view === "predictions" ? (
            <motion.div key="pred"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.35 }}
              className="flex-1 flex flex-col gap-2 min-h-0">
              {/* header */}
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-[12px] font-bold leading-tight" style={{ color: C.white, fontFamily: SANS }}>
                    {en ? "Sales & Forecast" : "Ventas y Pronóstico"}
                  </span>
                  <span className="text-[7.5px] uppercase tracking-wider mt-0.5" style={{ color: C.amber, fontFamily: MONO }}>
                    ✦ {en ? "Powered by RUMBO" : "Modelos de RUMBO"}
                  </span>
                </div>
              </div>

              {/* filter chips */}
              <motion.div
                initial={{ opacity: 0, y: 4 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: 0.06, duration: 0.3 }}
                className="flex items-center gap-1.5 flex-wrap">
                <span className="flex items-center justify-center" style={{ color: "rgba(245,247,250,0.6)" }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 5h18M6 12h12M10 19h4" />
                  </svg>
                </span>
                {[
                  { label: en ? "Product" : "Producto", val: "SKU-A21", dropdown: true },
                  { label: en ? "Store" : "Tienda", val: "04", dropdown: true },
                  { label: en ? "From" : "Desde", val: "01/03" },
                  { label: en ? "To" : "Hasta", val: "12/03" },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-1 rounded-md px-1.5 py-1"
                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                    <span className="text-[7px] uppercase tracking-wider" style={{ color: "rgba(245,247,250,0.45)", fontFamily: MONO }}>{f.label}</span>
                    <span className="text-[8.5px] font-semibold" style={{ color: C.white, fontFamily: SANS }}>{f.val}</span>
                    {f.dropdown && <span className="text-[7px]" style={{ color: "rgba(245,247,250,0.4)" }}>▾</span>}
                  </div>
                ))}
              </motion.div>

              {/* KPIs */}
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { l: en ? "Units sold" : "Unidades vendidas", v: "13.800" },
                  { l: en ? "Daily avg" : "Promedio diario", v: "1.150" },
                  { l: en ? "Forecast · next 7 days" : "Pronóstico · próx. 7 días", v: "8.400", accent: true },
                ].map((k, i) => (
                  <motion.div key={i}
                    initial={{ opacity: 0, y: 4 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ delay: 0.1 + i * 0.05, duration: 0.3 }}
                    className="rounded-md p-1.5 text-center" style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${k.accent ? "rgba(255,200,87,0.5)" : "rgba(255,200,87,0.18)"}` }}>
                    <span className="block text-[7px] uppercase tracking-wider leading-none mb-1" style={{ color: "rgba(245,247,250,0.5)", fontFamily: MONO }}>
                      {k.l}
                    </span>
                    <span className="block text-[13px] font-bold leading-none" style={{ color: k.accent ? C.amber : C.white, fontFamily: SANS, textShadow: k.accent ? "0 0 8px rgba(255,200,87,0.25)" : "none" }}>{k.v}</span>
                  </motion.div>
                ))}
              </div>

              {/* chart */}
              <motion.div
                initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: 0.28, duration: 0.35 }}
                className="rounded-md p-2.5 flex-1 flex flex-col min-h-0"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,200,87,0.2)" }}>
                {/* legend */}
                <div className="flex items-center gap-3 mb-1">
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-[2px] rounded-full" style={{ background: "rgba(245,247,250,0.7)" }} />
                    <span className="text-[7.5px] uppercase tracking-wider" style={{ color: "rgba(245,247,250,0.6)", fontFamily: MONO }}>{en ? "Actual" : "Real"}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-[2px] rounded-full" style={{ background: C.amber }} />
                    <span className="text-[7.5px] uppercase tracking-wider" style={{ color: C.amber, fontFamily: MONO }}>{en ? "Forecast" : "Pronóstico"}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-3 h-1.5 rounded-sm" style={{ background: C.amber, opacity: 0.22 }} />
                    <span className="text-[7.5px] uppercase tracking-wider" style={{ color: "rgba(245,247,250,0.6)", fontFamily: MONO }}>{en ? "95% CI" : "IC 95%"}</span>
                  </div>
                  <span className="ml-auto text-[7px] uppercase tracking-wider" style={{ color: "rgba(245,247,250,0.4)", fontFamily: MONO }}>
                    SKU-A21 · {en ? "Store" : "Tienda"} 04
                  </span>
                </div>
                {/* chart svg
                  Layout (viewBox 240x116):
                  - X range: 01/02 (x=0) → 12/03 (x=240). Forecast cutoff at x=140.
                */}
                <svg viewBox="0 0 240 102" preserveAspectRatio="none" className="w-full flex-1">
                  {/* gridlines */}
                  {[20, 50, 80].map((y) => (
                    <line key={y} x1="0" y1={y} x2="240" y2={y} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                  ))}
                  {/* axes */}
                  <line x1="0" y1="0" x2="0" y2="100" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
                  <line x1="0" y1="100" x2="240" y2="100" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
                  {/* forecast cutoff (only vertical line that stays) */}
                  <line x1="140" y1="0" x2="140" y2="100" stroke="rgba(255,255,255,0.3)" strokeWidth="0.8" strokeDasharray="3 3" />

                  {/* confidence band — natural continuation, widens with time
                       forecast points (7 days, x from 140 to 240):
                         (140,58) (156,63) (174,56) (191,62) (207,55) (224,60) (240,57)
                       half-width grows: ±5 / ±7 / ±9 / ±11 / ±13 / ±15 / ±17 */}
                  <motion.path
                    initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-40px" }} transition={{ delay: 0.4, duration: 0.6 }}
                    d="M 140,53 L 156,56 L 174,47 L 191,51 L 207,42 L 224,45 L 240,40
                       L 240,74 L 224,75 L 207,68 L 191,73 L 174,65 L 156,70 L 140,63 Z"
                    fill="rgba(255,200,87,0.10)" />
                  <path d="M 140,53 L 156,56 L 174,47 L 191,51 L 207,42 L 224,45 L 240,40"
                    fill="none" stroke="rgba(230,176,78,0.55)" strokeWidth="0.8" strokeDasharray="2 2" />
                  <path d="M 140,63 L 156,70 L 174,65 L 191,73 L 207,68 L 224,75 L 240,74"
                    fill="none" stroke="rgba(230,176,78,0.55)" strokeWidth="0.8" strokeDasharray="2 2" />

                  {/* historical (light gray) — realistic jagged daily data */}
                  <path d="M 0,72 L 14,68 L 28,74 L 42,62 L 56,71 L 70,66 L 84,77 L 98,60 L 112,69 L 126,55 L 140,58"
                    fill="none" stroke="rgba(245,247,250,0.78)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />

                  {/* forecast — clean amber line with day-to-day variation, slight upward trend */}
                  <motion.path
                    initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true, margin: "-40px" }} transition={{ delay: 0.5, duration: 1.0, ease: "easeOut" }}
                    d="M 140,58 L 156,63 L 174,56 L 191,62 L 207,55 L 224,60 L 240,57"
                    fill="none" stroke={C.amber} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {/* HTML date labels — exact same style as filter chip values */}
                <div className="relative mt-1 h-3">
                  <span className="absolute left-0 top-0 text-[8.5px] font-semibold" style={{ color: C.white, fontFamily: SANS }}>01/03</span>
                  <span className="absolute top-0 text-[8.5px] font-semibold" style={{ left: "58.3%", transform: "translateX(-50%)", color: C.white, fontFamily: SANS }}>12/03</span>
                  <span className="absolute right-0 top-0 text-[8.5px] font-semibold" style={{ color: C.white, fontFamily: SANS }}>19/03</span>
                </div>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div key="repo"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.4 }}
              className="flex-1 flex flex-col gap-2 min-h-0 relative">
              {/* automation overlay */}
              {view === "automation" && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                  className="absolute top-0 right-0 z-20 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md"
                  style={{ background: "rgba(74,222,128,0.15)", border: "1px solid rgba(74,222,128,0.4)", boxShadow: "0 4px 14px rgba(74,222,128,0.2)" }}>
                  <span className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold" style={{ background: "#4ade80", color: C.navy }}>✓</span>
                  <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: "#4ade80", fontFamily: MONO }}>
                    {en ? "Sent" : "Enviadas"}
                  </span>
                </motion.div>
              )}
              {/* header row: title + action buttons */}
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-[12px] font-bold leading-tight" style={{ color: C.white, fontFamily: SANS }}>
                    {en ? "Replenishment orders" : "Órdenes de reposición"}
                  </span>
                  <span className="text-[7.5px] uppercase tracking-wider mt-0.5" style={{ color: C.amber, fontFamily: MONO }}>
                    ✦ {en ? "Calculated by RUMBO" : "Calculadas por RUMBO"}
                  </span>
                </div>
                <div className="flex items-center gap-1" style={{ opacity: view === "automation" ? 0 : 1 }}>
                  <span className="text-[8px] font-bold px-2 py-1 rounded-md flex items-center gap-1"
                    style={{ background: "rgba(255,200,87,0.12)", border: "1px solid rgba(255,200,87,0.3)", color: C.amber, fontFamily: SANS }}>
                    + {en ? "New" : "Nueva"}
                  </span>
                  <span className="text-[8px] font-bold px-2 py-1 rounded-md flex items-center gap-1"
                    style={{ background: "rgba(255,200,87,0.12)", border: "1px solid rgba(255,200,87,0.3)", color: C.amber, fontFamily: SANS }}>
                    ↓ {en ? "Export" : "Descargar"}
                  </span>
                </div>
              </div>

              {/* KPIs */}
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { label: en ? "Orders" : "Órdenes", val: "1,248", accent: true },
                  { label: en ? "Units" : "Unidades", val: "38.6k", accent: false },
                  { label: en ? "Captation" : "Captación", val: "94%", accent: false },
                ].map((k, i) => (
                  <motion.div key={i}
                    initial={{ opacity: 0, y: 4 }} whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ delay: 0.05 + i * 0.06, duration: 0.3 }}
                    className="rounded-md p-1.5 text-center" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,200,87,0.18)" }}>
                    <span className="block text-[7.5px] uppercase tracking-wider leading-none mb-1" style={{ color: "rgba(245,247,250,0.5)", fontFamily: MONO }}>
                      {k.label}
                    </span>
                    <span className="block text-[11px] md:text-[13px] font-bold leading-none"
                      style={{ color: k.accent ? C.amber : C.white, fontFamily: SANS }}>
                      {k.val}
                    </span>
                  </motion.div>
                ))}
              </div>

              {/* filter chips */}
              <motion.div
                initial={{ opacity: 0, y: 4 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: 0.14, duration: 0.3 }}
                className="flex items-center gap-1.5 flex-wrap">
                <span className="flex items-center justify-center" style={{ color: "rgba(245,247,250,0.6)" }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 5h18M6 12h12M10 19h4" />
                  </svg>
                </span>
                {[
                  { label: en ? "Product" : "Producto", val: en ? "All" : "Todos", dropdown: true },
                  { label: en ? "Store" : "Tienda", val: en ? "All" : "Todas", dropdown: true },
                  { label: en ? "From" : "Desde", val: "01/03", dropdown: false },
                  { label: en ? "To" : "Hasta", val: "12/03", dropdown: false },
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-1 rounded-md px-1.5 py-1"
                    style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                    <span className="text-[7px] uppercase tracking-wider" style={{ color: "rgba(245,247,250,0.45)", fontFamily: MONO }}>{f.label}</span>
                    <span className="text-[8.5px] font-semibold" style={{ color: C.white, fontFamily: SANS }}>{f.val}</span>
                    {f.dropdown && <span className="text-[7px]" style={{ color: "rgba(245,247,250,0.4)" }}>▾</span>}
                  </div>
                ))}
                <div className="flex items-center gap-1 rounded-md px-2 py-1"
                  style={{ background: "rgba(255,200,87,0.14)", border: "1px solid rgba(255,200,87,0.35)" }}>
                  <span className="text-[8.5px] font-bold" style={{ color: C.amber, fontFamily: SANS }}>
                    {en ? "Today" : "Hoy"}
                  </span>
                </div>
              </motion.div>

              {/* replenishment orders table */}
              <motion.div
                initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: 0.22, duration: 0.35 }}
                className="rounded-md flex-1 flex flex-col overflow-hidden min-h-0"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,200,87,0.2)" }}>
                {/* header */}
                {(() => {
                  const cols = "repeat(7, minmax(0, 1fr))";
                  return (
                    <>
                      <div className="grid gap-1 px-2 py-1.5 items-center"
                        style={{ gridTemplateColumns: cols }}>
                        <span className="text-[7px] uppercase tracking-wider font-bold text-center" style={{ color: "rgba(245,247,250,0.5)", fontFamily: MONO }}>
                          {en ? "Date" : "Fecha"}
                        </span>
                        <span className="text-[7px] uppercase tracking-wider font-bold text-center" style={{ color: "rgba(245,247,250,0.5)", fontFamily: MONO }}>
                          {en ? "Product" : "Producto"}
                        </span>
                        <span className="text-[7px] uppercase tracking-wider font-bold text-center" style={{ color: "rgba(245,247,250,0.5)", fontFamily: MONO }}>
                          {en ? "Store" : "Tienda"}
                        </span>
                        <span className="text-[7px] uppercase tracking-wider font-bold self-stretch flex items-center justify-center -my-1.5 pt-1.5 pb-1 rounded-t-md"
                          style={{
                            color: C.amber,
                            fontFamily: MONO,
                            background: "rgba(255,200,87,0.10)",
                            borderTop: `1.5px solid ${C.amber}`,
                            borderLeft: `1.5px solid ${C.amber}`,
                            borderRight: `1.5px solid ${C.amber}`,
                          }}>
                          ✦ {en ? "Quantity" : "Cantidad"}
                        </span>
                        <span className="text-[7px] uppercase tracking-wider font-bold text-center" style={{ color: "rgba(245,247,250,0.5)", fontFamily: MONO }}>
                          {en ? "Coverage" : "Cobertura"}
                        </span>
                        <span className="text-[7px] uppercase tracking-wider font-bold text-center" style={{ color: "rgba(245,247,250,0.5)", fontFamily: MONO }}>
                          {en ? "Status" : "Estado"}
                        </span>
                        <span className="text-[7px] uppercase tracking-wider font-bold text-center" style={{ color: "rgba(245,247,250,0.5)", fontFamily: MONO }}>
                          {en ? "Prediction" : "Predicción"}
                        </span>
                      </div>
                      {/* rows */}
                      <div className="flex-1 flex flex-col">
                        {[
                          { date: "12/03", sku: "SKU-A21", store: "04", qty: "8.400",  cov: "7d",  status: en ? "Accepted" : "Aceptado" },
                          { date: "12/03", sku: "SKU-D14", store: "04", qty: "12.600", cov: "7d",  status: en ? "Accepted" : "Aceptado" },
                          { date: "12/03", sku: "SKU-J16", store: "04", qty: "5.750",  cov: "14d", status: en ? "Accepted" : "Aceptado" },
                          { date: "12/03", sku: "SKU-K22", store: "04", qty: "16.200", cov: "7d",  status: en ? "Accepted" : "Aceptado" },
                          { date: "12/03", sku: "SKU-B07", store: "12", qty: "10.500", cov: "14d", status: en ? "Accepted" : "Aceptado" },
                          { date: "12/03", sku: "SKU-C33", store: "12", qty: "30.000", cov: "7d",  status: en ? "Accepted" : "Aceptado" },
                          { date: "12/03", sku: "SKU-E55", store: "12", qty: "6.200",  cov: "14d", status: en ? "Accepted" : "Aceptado" },
                          { date: "12/03", sku: "SKU-F18", store: "12", qty: "18.750", cov: "7d",  status: en ? "Accepted" : "Aceptado" },
                          { date: "12/03", sku: "SKU-G42", store: "27", qty: "4.300",  cov: "7d",  status: en ? "Accepted" : "Aceptado" },
                          { date: "12/03", sku: "SKU-H09", store: "27", qty: "22.100", cov: "14d", status: en ? "Accepted" : "Aceptado" },
                          { date: "12/03", sku: "SKU-L31", store: "27", qty: "9.800",  cov: "7d",  status: en ? "Accepted" : "Aceptado" },
                          { date: "12/03", sku: "SKU-M48", store: "27", qty: "14.500", cov: "14d", status: en ? "Accepted" : "Aceptado" },
                        ].map((r, i) => (
                          <motion.div key={i}
                            initial={{ opacity: 0, x: -4 }} whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ delay: 0.3 + i * 0.07, duration: 0.28 }}
                            className="grid gap-1 px-2 py-1 items-center"
                            style={{ gridTemplateColumns: cols }}>
                            <span className="text-[8px] font-mono text-center" style={{ color: "rgba(245,247,250,0.55)" }}>{r.date}</span>
                            <span className="text-[8.5px] font-mono truncate text-center" style={{ color: "rgba(245,247,250,0.85)" }}>{r.sku}</span>
                            <span className="text-[8px] font-mono text-center" style={{ color: "rgba(245,247,250,0.6)" }}>{r.store}</span>
                            {(() => {
                              const isLast = i === 11;
                              return (
                                <span className={`text-[13px] font-extrabold tracking-tight self-stretch flex items-center justify-center -my-1 ${isLast ? "rounded-b-md" : ""}`}
                                  style={{
                                    color: C.amber,
                                    fontFamily: SANS,
                                    background: "rgba(255,200,87,0.10)",
                                    borderLeft: `1.5px solid ${C.amber}`,
                                    borderRight: `1.5px solid ${C.amber}`,
                                    borderBottom: isLast ? `1.5px solid ${C.amber}` : "none",
                                    textShadow: "0 0 8px rgba(255,200,87,0.25)",
                                  }}>{r.qty}</span>
                              );
                            })()}
                            <span className="text-[8px] text-center" style={{ color: "rgba(245,247,250,0.5)", fontFamily: MONO }}>{r.cov}</span>
                            <span className="text-[7.5px] font-bold inline-flex items-center justify-center px-1 py-0.5 rounded justify-self-center"
                              style={{ background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.3)", color: "#4ade80" }}>
                              {r.status}
                            </span>
                            <span className="relative text-[7.5px] font-semibold text-center underline decoration-dotted" style={{ color: C.amber, fontFamily: SANS }}>
                              {en ? "view →" : "ver →"}
                              {clickPredictionLink && i === 0 && (
                                <>
                                  <motion.span
                                    initial={{ scale: 0.6, opacity: 0.9 }}
                                    animate={{ scale: 1.7, opacity: 0 }}
                                    transition={{ duration: 0.55, ease: "easeOut" }}
                                    className="absolute pointer-events-none rounded-full"
                                    style={{
                                      left: "50%", top: "50%", width: 22, height: 22, marginLeft: -11, marginTop: -11,
                                      border: `2px solid ${C.amber}`,
                                    }}
                                  />
                                  <motion.span
                                    initial={{ opacity: 0, x: 8, y: 8 }}
                                    animate={{ opacity: [0, 1, 1, 0], x: [8, 0, 0, 0], y: [8, 0, 0, 0] }}
                                    transition={{ duration: 0.6, ease: "easeOut", times: [0, 0.3, 0.7, 1] }}
                                    className="absolute pointer-events-none"
                                    style={{ left: "55%", top: "55%" }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill={C.amber} stroke={C.navy} strokeWidth="1.2">
                                      <path d="M4 2 L4 18 L9 14 L12 21 L15 19.5 L12 13 L19 13 Z" />
                                    </svg>
                                  </motion.span>
                                </>
                              )}
                            </span>
                          </motion.div>
                        ))}
                      </div>
                    </>
                  );
                })()}
                {/* footer hint */}
                <div className="px-2 py-1 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                  <span className="text-[7.5px] uppercase tracking-wider" style={{ color: "rgba(245,247,250,0.35)", fontFamily: MONO }}>
                    {en ? "+ 1,243 more orders" : "+ 1.243 órdenes más"}
                  </span>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Tab({ label, active }) {
  return (
    <div className="flex items-center gap-2 rounded-md px-2 py-1.5 transition-all duration-300"
      style={{
        background: active ? "rgba(255,200,87,0.14)" : "transparent",
        border: active ? "1px solid rgba(255,200,87,0.35)" : "1px solid transparent",
      }}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-all duration-300"
        style={{
          background: active ? C.amber : "rgba(255,255,255,0.25)",
          boxShadow: active ? `0 0 6px ${C.amber}` : "none",
        }} />
      <span className="text-[10px] md:text-[11px] font-semibold leading-tight transition-colors duration-300"
        style={{ color: active ? C.amber : "rgba(245,247,250,0.55)", fontFamily: SANS }}>
        {label}
      </span>
    </div>
  );
}

/* ─── Key Features accordion ─── */
function KeyFeatures({ en }) {
  const features = en ? [
    {
      title: "Automated orders",
      summary: "Start the day knowing what to order, where and how much.",
      body: "Every day RUMBO generates the replenishment orders for each store, with the optimal quantity of every product and putting first the products with the most impact on the operation. Your team reviews them, adjusts what it needs and sends them.",
      view: "automation",
    },
    {
      title: "Accurate demand forecasting",
      summary: "The basis of every suggested quantity.",
      body: "Our models forecast the demand of each product in each store for the next replenishment cycle, and each order shows the forecast behind its quantity.",
      view: "predictions",
    },
    {
      title: "Radar",
      summary: "Follow everything, from one place.",
      body: "Where every order stands — created today, on the way, late — and the day's insights: products out of stock or at risk, overstock, orders not reviewed and unusual sales, each with the action to take.",
      view: "radar",
    },
    {
      title: "Stockouts and overstock",
      summary: "What stockouts and overstock cost, in money.",
      body: "RUMBO's models detect stockouts and overstock for each product in each store and turn them into money: sales not made for lack of stock and money tied up in merchandise that does not move.",
      view: "losses",
    },
    {
      title: "General",
      summary: "Stock management, at a glance.",
      body: "The key indicators to follow at any moment how stock is evolving, together with RUMBO's impact in money, month by month.",
      view: "general",
    },
  ] : [
    {
      title: "Órdenes automatizadas",
      summary: "Empezar el día sabiendo qué pedir, dónde y cuánto.",
      body: "Todos los días RUMBO genera las órdenes de reposición de cada tienda, con la cantidad óptima de cada producto y priorizando los de mayor impacto en la operación. Tu equipo las revisa, ajusta lo que necesite y las envía.",
      view: "automation",
    },
    {
      title: "Predicción precisa de demanda",
      summary: "La base de cada cantidad sugerida.",
      body: "Nuestros modelos predicen la demanda de cada producto en cada tienda para el próximo ciclo de reposición, y cada orden muestra la predicción detrás de su cantidad.",
      view: "predictions",
    },
    {
      title: "Radar",
      summary: "El seguimiento de todo, en un solo lugar.",
      body: "Dónde está cada pedido —generados hoy, en camino, atrasados— y los insights del día: productos sin stock o en riesgo, sobrestock, órdenes sin revisar y ventas inusuales, cada uno con la acción a tomar.",
      view: "radar",
    },
    {
      title: "Quiebres y sobrestock",
      summary: "Lo que cuestan los quiebres y el sobrestock, en dinero.",
      body: "Los modelos de RUMBO detectan los quiebres de stock y el sobrestock de cada producto en cada tienda, y los traducen a dinero: ventas que no se hicieron por falta de stock y dinero inmovilizado en mercadería que no rota.",
      view: "losses",
    },
    {
      title: "General",
      summary: "La gestión de stock, de un vistazo.",
      body: "Los indicadores clave para seguir en todo momento cómo evoluciona el stock, junto con el impacto de RUMBO en dinero, mes a mes.",
      view: "general",
    },
  ];

  const [active, setActive] = useState(0);
  const [displayView, setDisplayView] = useState(features[0].view);
  const [predictionClick, setPredictionClick] = useState(false);

  useEffect(() => {
    const targetView = features[active].view;
    if (targetView === "predictions" && displayView !== "predictions") {
      // Show replenishment view with click on "ver" link, then switch
      setDisplayView("replenishment");
      setPredictionClick(true);
      const t1 = setTimeout(() => setPredictionClick(false), 600);
      const t2 = setTimeout(() => setDisplayView("predictions"), 700);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    setDisplayView(targetView);
    setPredictionClick(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-8 lg:gap-10 items-stretch">
      {/* LEFT — animated platform mock */}
      <div className="relative order-2 lg:order-1 flex items-center">
        <div className="w-full">
          <PlatformMock en={en} view={displayView} clickPredictionLink={predictionClick} />
        </div>
      </div>

      {/* RIGHT — accordion */}
      <div className="order-1 lg:order-2 flex flex-col justify-center">
        {features.map((f, i) => {
          const open = active === i;
          return (
            <div key={i} className="border-b" style={{ borderColor: "rgba(11,31,58,0.12)" }}>
              <button
                type="button"
                onClick={() => setActive(i)}
                className="w-full flex items-center justify-between gap-4 py-5 text-left transition-colors"
                style={{ color: C.navy }}>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em]"
                      style={{ color: open ? C.amberDark : "#94a3b8", fontFamily: MONO }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h4 className={`text-[17px] md:text-[19px] leading-tight transition-all ${open ? "font-bold" : "font-semibold"}`}
                      style={{ color: open ? C.navy : C.grayDark }}>
                      {f.title}
                    </h4>
                  </div>
                </div>
                <span className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                  style={{
                    background: open ? C.navy : "transparent",
                    color: open ? C.amber : C.grayDark,
                    border: open ? "none" : `1px solid rgba(11,31,58,0.2)`,
                    transform: open ? "rotate(180deg)" : "none",
                  }}>
                  ⌃
                </span>
              </button>
              <motion.div
                initial={false}
                animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                style={{ overflow: "hidden" }}>
                <div className="pb-5 pr-10">
                  <p className="text-[14px] font-semibold mb-2" style={{ color: C.amberDark }}>
                    {f.summary}
                  </p>
                  <p className="text-[14px] leading-relaxed" style={{ color: C.grayDark }}>
                    {f.body}
                  </p>
                </div>
              </motion.div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Benefit illustrations ─── */
export function StockoutIllustration() {
  // ── helpers ───────────────────────────────────────────────
  const SinStockSign = ({ x, y, scale = 1 }) => (
    <g transform={`translate(${x}, ${y}) scale(${scale})`}>
      <rect x="0" y="0" width="28" height="14" rx="1" fill="#FFFFFF" />
      <text x="14" y="6" fontSize="4" textAnchor="middle" fill="#EF4444" fontWeight="800" fontFamily="Inter, sans-serif">SIN</text>
      <text x="14" y="11" fontSize="4" textAnchor="middle" fill="#EF4444" fontWeight="800" fontFamily="Inter, sans-serif">STOCK</text>
    </g>
  );
  const ShelfBoard = ({ y, x0, x1 }) => (
    <g>
      <rect x={x0} y={y} width={x1 - x0} height="2.4" fill="#5A6478" />
      <rect x={x0} y={y + 2.4} width={x1 - x0} height="0.8" fill="rgba(0,0,0,0.4)" />
    </g>
  );
  const PriceTag = ({ x, y }) => (
    <rect x={x} y={y} width="7" height="2" fill="rgba(255,255,255,0.55)" rx="0.4" />
  );
  // tiny generic products
  const Bottle = ({ x, y, color, label }) => (
    <g transform={`translate(${x}, ${y})`}>
      <rect x="3" y="0" width="6" height="2" rx="0.7" fill="#FFD24A" />
      <rect x="1.5" y="2" width="9" height="17" rx="1" fill={color} />
      <rect x="2" y="9" width="8" height="5" fill="#FFFFFF" />
      {label && <text x="6" y="13" fontSize="3" textAnchor="middle" fill="#EF4444" fontWeight="800" fontFamily="Inter, sans-serif">{label}</text>}
    </g>
  );
  const Carton = ({ x, y }) => (
    <g transform={`translate(${x}, ${y})`}>
      <path d="M1.5 4 L6 0 L10.5 4 L10.5 22 L1.5 22 Z" fill="#FFFFFF" />
      <rect x="1.5" y="8" width="9" height="8" fill="#5CB8FF" />
      <text x="6" y="13.5" fontSize="3.2" textAnchor="middle" fill="#FFFFFF" fontWeight="800" fontFamily="Inter, sans-serif">MILK</text>
    </g>
  );
  const Yogurt = ({ x, y }) => (
    <g transform={`translate(${x}, ${y})`}>
      <path d="M0 2 L10 2 L8.8 12 L1.2 12 Z" fill="#FFFFFF" />
      <rect x="-0.4" y="0.4" width="10.8" height="2.4" fill="#EF4444" />
    </g>
  );
  const Tupper = ({ x, y, color }) => (
    <g transform={`translate(${x}, ${y})`}>
      <rect x="0" y="0" width="11" height="11" rx="1.2" fill={color} />
      <rect x="-0.8" y="-0.8" width="12.6" height="2.4" fill="#FFFFFF" />
    </g>
  );
  const Juice = ({ x, y }) => (
    <g transform={`translate(${x}, ${y})`}>
      <rect x="0" y="0" width="8.5" height="17" rx="0.6" fill="#FFD24A" />
      <rect x="0.8" y="5" width="6.9" height="5" fill="#0B1F3A" />
    </g>
  );
  const Cheese = ({ x, y }) => (
    <g transform={`translate(${x}, ${y})`}>
      <circle cx="7" cy="7" r="6" fill="#FFD24A" />
      <circle cx="7" cy="7" r="4" fill="#EF4444" />
    </g>
  );

  // Shelf rows (y) for both scenes
  const ROWS = [40, 70, 100, 130];

  // ── EMPTY SHELF (left scene) ──────────────────────────────
  const EmptyScene = ({ x0 }) => {
    const x1 = x0 + 88;
    return (
      <g>
        {ROWS.map((y, i) => <ShelfBoard key={i} y={y} x0={x0} x1={x1} />)}
        {/* row 1 — 2 bottles left + sign */}
        <Bottle x={x0 + 4} y={18} color="#5CB8FF" label="MILK" />
        <SinStockSign x={x0 + 52} y={20} />
        {/* row 2 — sign + juice */}
        <SinStockSign x={x0 + 4} y={50} />
        <Juice x={x0 + 55} y={50} />
        {/* row 3 — 2 yogurts + sign */}
        <Yogurt x={x0 + 6} y={86} />
        <Yogurt x={x0 + 20} y={86} />
        <SinStockSign x={x0 + 52} y={86} />
        {/* row 4 — 1 tupper + sign */}
        <Tupper x={x0 + 6} y={116} color="#4ade80" />
        <SinStockSign x={x0 + 30} y={114} />
        {/* price tags (sparse) */}
        {ROWS.map((y, i) => (
          <g key={i}>
            <PriceTag x={x0 + 4} y={y + 3.5} />
            <PriceTag x={x0 + 42} y={y + 3.5} />
            <PriceTag x={x0 + 76} y={y + 3.5} />
          </g>
        ))}
      </g>
    );
  };

  // ── FULL SHELF (right scene) ──────────────────────────────
  const FullScene = ({ x0 }) => {
    const x1 = x0 + 88;
    return (
      <g>
        {ROWS.map((y, i) => <ShelfBoard key={i} y={y} x0={x0} x1={x1} />)}
        {/* row 1 — bottles + carton */}
        <Bottle x={x0 + 4} y={18} color="#5CB8FF" label="MILK" />
        <Bottle x={x0 + 18} y={18} color="#FFD24A" label="MILK" />
        <Bottle x={x0 + 32} y={18} color="#EF4444" />
        <Bottle x={x0 + 46} y={18} color="#4ade80" />
        <Carton x={x0 + 60} y={18} />
        <Bottle x={x0 + 74} y={18} color="#A8B6CC" />
        {/* row 2 — juice line */}
        <Juice x={x0 + 4} y={50} />
        <Juice x={x0 + 16} y={50} />
        <Juice x={x0 + 28} y={50} />
        <Carton x={x0 + 44} y={48} />
        <Bottle x={x0 + 60} y={50} color="#5CB8FF" />
        <Juice x={x0 + 74} y={50} />
        {/* row 3 — yogurts */}
        {[4, 16, 28, 40, 52, 64, 76].map((dx, i) => (
          <Yogurt key={i} x={x0 + dx} y={86} />
        ))}
        {/* row 4 — tuppers + yogurts + cheese */}
        <Tupper x={x0 + 4} y={116} color="#4ade80" />
        <Tupper x={x0 + 18} y={116} color="#5CB8FF" />
        <Tupper x={x0 + 32} y={116} color="#EF4444" />
        <Yogurt x={x0 + 48} y={117} />
        <Yogurt x={x0 + 60} y={117} />
        <Cheese x={x0 + 73} y={115} />
        {/* price tags */}
        {ROWS.map((y, i) => (
          <g key={i}>
            <PriceTag x={x0 + 4} y={y + 3.5} />
            <PriceTag x={x0 + 22} y={y + 3.5} />
            <PriceTag x={x0 + 40} y={y + 3.5} />
            <PriceTag x={x0 + 58} y={y + 3.5} />
            <PriceTag x={x0 + 76} y={y + 3.5} />
          </g>
        ))}
      </g>
    );
  };

  return (
    <svg viewBox="0 0 280 160" className="w-full h-full">
      {/* dark backdrop */}
      <rect x="0" y="0" width="280" height="160" fill="#0B1F3A" />
      <rect x="0" y="155" width="280" height="5" fill="#06122A" />

      {/* LEFT — empty shelf */}
      <EmptyScene x0={6} />

      {/* CENTER — FARO logo */}
      <g>
        <circle cx="140" cy="80" r="26" fill="rgba(255,200,87,0.10)" />
        <image href={faroLogo} x="112" y="58" width="56" height="44" preserveAspectRatio="xMidYMid meet" />
      </g>

      {/* RIGHT — full shelf */}
      <FullScene x0={186} />

      {/* ANTES / DESPUÉS labels — below scenes */}
      <g>
        <rect x="22" y="144" width="56" height="14" rx="2" fill="rgba(245,247,250,0.08)" stroke="rgba(245,247,250,0.45)" strokeWidth="1" />
        <text x="50" y="154" fontSize="8" textAnchor="middle" fill="rgba(245,247,250,0.7)" fontWeight="800" fontFamily="Inter, sans-serif" letterSpacing="0.5">ANTES</text>
      </g>
      <g>
        <rect x="202" y="144" width="64" height="14" rx="2" fill="rgba(255,200,87,0.18)" stroke="#FFC857" strokeWidth="1" />
        <text x="234" y="154" fontSize="8" textAnchor="middle" fill="#FFC857" fontWeight="800" fontFamily="Inter, sans-serif" letterSpacing="0.5">DESPUÉS</text>
      </g>
    </svg>
  );
}

export function OverstockIllustration() {
  // ── building outline ─────────────────────────────────────
  const Warehouse = ({ x0 }) => {
    const w = 88, x1 = x0 + w;
    return (
      <g>
        {/* roof line */}
        <line x1={x0} y1="22" x2={x1} y2="22" stroke="rgba(245,247,250,0.45)" strokeWidth="1" />
        {/* side posts */}
        <line x1={x0} y1="22" x2={x0} y2="135" stroke="rgba(245,247,250,0.45)" strokeWidth="1" />
        <line x1={x1} y1="22" x2={x1} y2="135" stroke="rgba(245,247,250,0.45)" strokeWidth="1" />
        {/* floor */}
        <line x1={x0 - 2} y1="135" x2={x1 + 2} y2="135" stroke="rgba(255,200,87,0.7)" strokeWidth="1.4" />
        {/* faint diagonal hatches on side walls suggesting depth */}
        {[26, 36, 46, 56, 66, 76, 86, 96, 106, 116, 126].map((y, i) => (
          <g key={i}>
            <line x1={x0} y1={y} x2={x0 + 5} y2={y - 4} stroke="rgba(245,247,250,0.1)" strokeWidth="0.6" />
            <line x1={x1} y1={y} x2={x1 - 5} y2={y - 4} stroke="rgba(245,247,250,0.1)" strokeWidth="0.6" />
          </g>
        ))}
      </g>
    );
  };
  // single carton box (active = amber, discarded = brown/red, no markings)
  const Box = ({ x, y, w = 14, h = 12, tone = 0.55, expired = false, discarded = false }) => {
    const fillCol = discarded ? `rgba(160, 80, 60, ${tone})` : `rgba(255, 200, 87, ${tone})`;
    const strokeCol = discarded ? "rgba(239,68,68,0.7)" : "rgba(255,200,87,0.85)";
    return (
      <g>
        <rect x={x} y={y} width={w} height={h} fill={fillCol} stroke={strokeCol} strokeWidth={discarded ? 0.9 : 0.6} />
        <line x1={x} y1={y + h / 2} x2={x + w} y2={y + h / 2} stroke="rgba(11,31,58,0.45)" strokeWidth="0.5" />
        <line x1={x + w / 2} y1={y} x2={x + w / 2} y2={y + h / 2} stroke="rgba(11,31,58,0.45)" strokeWidth="0.4" />
        {expired && !discarded && (
          <g>
            <line x1={x + 2.5} y1={y + 2.5} x2={x + w - 2.5} y2={y + h - 2.5} stroke="#EF4444" strokeWidth="1.4" strokeLinecap="round" />
            <line x1={x + w - 2.5} y1={y + 2.5} x2={x + 2.5} y2={y + h - 2.5} stroke="#EF4444" strokeWidth="1.4" strokeLinecap="round" />
          </g>
        )}
      </g>
    );
  };
  // cobweb (suggests stagnation)
  const Cobweb = ({ x, y }) => (
    <g stroke="rgba(245,247,250,0.35)" strokeWidth="0.5" fill="none">
      <path d={`M${x} ${y} L${x + 10} ${y} L${x + 10} ${y + 10}`} />
      <path d={`M${x} ${y} L${x + 10} ${y + 10}`} />
      <path d={`M${x + 4} ${y} L${x + 10} ${y + 6}`} />
      <path d={`M${x} ${y + 4} L${x + 6} ${y + 10}`} />
    </g>
  );
  // pallet
  const Pallet = ({ x, y }) => (
    <g>
      <rect x={x} y={y} width="24" height="3" fill="rgba(255,200,87,0.45)" />
      <rect x={x} y={y + 3} width="3" height="3" fill="rgba(255,200,87,0.35)" />
      <rect x={x + 10.5} y={y + 3} width="3" height="3" fill="rgba(255,200,87,0.35)" />
      <rect x={x + 21} y={y + 3} width="3" height="3" fill="rgba(255,200,87,0.35)" />
    </g>
  );
  // forklift (mini)
  const Forklift = ({ x, y }) => (
    <g transform={`translate(${x}, ${y})`}>
      {/* body */}
      <rect x="6" y="8" width="14" height="10" rx="1" fill="#FFC857" stroke="rgba(11,31,58,0.6)" strokeWidth="0.6" />
      {/* cabin */}
      <rect x="9" y="2" width="8" height="7" fill="rgba(11,31,58,0.55)" />
      {/* mast */}
      <line x1="22" y1="2" x2="22" y2="18" stroke="rgba(245,247,250,0.85)" strokeWidth="1" />
      {/* fork */}
      <line x1="22" y1="14" x2="30" y2="14" stroke="rgba(245,247,250,0.85)" strokeWidth="1" />
      <line x1="22" y1="17" x2="30" y2="17" stroke="rgba(245,247,250,0.85)" strokeWidth="1" />
      {/* wheels */}
      <circle cx="10" cy="20" r="2.5" fill="rgba(11,31,58,0.8)" stroke="rgba(245,247,250,0.6)" strokeWidth="0.6" />
      <circle cx="17" cy="20" r="2.5" fill="rgba(11,31,58,0.8)" stroke="rgba(245,247,250,0.6)" strokeWidth="0.6" />
    </g>
  );
  // flow arrow on floor
  const FlowArrow = ({ x, y, w = 22 }) => (
    <g>
      <line x1={x} y1={y} x2={x + w - 4} y2={y} stroke="#4ade80" strokeWidth="1.4" strokeLinecap="round" />
      <path d={`M${x + w - 6} ${y - 3} L${x + w} ${y} L${x + w - 6} ${y + 3}`} stroke="#4ade80" strokeWidth="1.4" fill="none" strokeLinejoin="round" strokeLinecap="round" />
    </g>
  );

  // ── LEFT (cramped, no rotation) ──────────────────────────
  const CrampedScene = ({ x0 }) => {
    // first column is set aside (discarded), rest packed densely
    const boxes = [];
    const cols = [2, 22, 35, 48, 61, 74]; // gap between col 0 and col 1 → "puesta afuera"
    const rows = [123, 111, 99, 87, 75, 63, 51];
    cols.forEach((cx, ci) => {
      const discarded = ci === 0;
      // discarded column only stacks 4 boxes; rest stacks the full height
      const colRows = discarded ? rows.slice(0, 4) : rows;
      colRows.forEach((cy, r) => {
        const seed = (cx * 7 + cy) % 11;
        const expired = !discarded && (seed === 3 || seed === 7);
        boxes.push({ x: x0 + cx, y: cy, expired, discarded });
      });
    });
    // extra spilled box on floor
    return (
      <g>
        <Warehouse x0={x0} />
        {boxes.map((b, i) => (
          <Box key={i} x={b.x} y={b.y} w={12} h={11} tone={0.5 + ((i % 3) * 0.1)} expired={b.expired} discarded={b.discarded} />
        ))}
        {/* VENCIDO label above the discarded column */}
        <g>
          <rect x={x0 - 6} y="72" width="28" height="11" rx="1.5" fill="#EF4444" />
          <text x={x0 + 8} y="79.5" fontSize="5.5" textAnchor="middle" fill="#FFFFFF" fontWeight="800" fontFamily="Inter, sans-serif" letterSpacing="0.4">VENCIDO</text>
          <line x1={x0 + 8} y1="83" x2={x0 + 8} y2="88" stroke="#EF4444" strokeWidth="0.9" strokeLinecap="round" />
        </g>
        {/* stagnation dust dots */}
        {[35, 50, 65].map((dx, i) => (
          <circle key={i} cx={x0 + dx} cy={134} r="0.7" fill="rgba(245,247,250,0.35)" />
        ))}
      </g>
    );
  };
  // helper width
  function w() { return 88; }

  // ── RIGHT (organized, rotating) ──────────────────────────
  const OrganizedScene = ({ x0 }) => {
    return (
      <g>
        <Warehouse x0={x0} />
        {/* two organized stacks with breathing room — leave middle for forklift */}
        <Pallet x={x0 + 4} y={130} />
        <Box x={x0 + 7} y={118} w={18} h={12} tone={0.55} />
        <Box x={x0 + 7} y={106} w={18} h={12} tone={0.45} />
        <Box x={x0 + 7} y={94}  w={18} h={12} tone={0.35} />

        <Pallet x={x0 + 60} y={130} />
        <Box x={x0 + 63} y={118} w={16} h={12} tone={0.55} />
        <Box x={x0 + 63} y={106} w={16} h={12} tone={0.45} />

        {/* forklift on the floor between stacks, carrying a box */}
        <g>
          <Forklift x={x0 + 30} y={113} />
          {/* box being lifted on the forks */}
          <Box x={x0 + 53} y={117} w={12} h={11} tone={0.7} />
        </g>

        {/* flow arrows above showing circulation */}
        <FlowArrow x={x0 + 16} y={80} />
        <FlowArrow x={x0 + 50} y={68} />
      </g>
    );
  };

  return (
    <svg viewBox="0 0 280 160" className="w-full h-full">
      {/* dark backdrop */}
      <rect x="0" y="0" width="280" height="160" fill="#0B1F3A" />
      <rect x="0" y="155" width="280" height="5" fill="#06122A" />

      {/* LEFT — cramped warehouse */}
      <CrampedScene x0={6} />

      {/* CENTER — FARO logo */}
      <g>
        <circle cx="140" cy="80" r="26" fill="rgba(255,200,87,0.10)" />
        <image href={faroLogo} x="112" y="58" width="56" height="44" preserveAspectRatio="xMidYMid meet" />
      </g>

      {/* RIGHT — organized warehouse */}
      <OrganizedScene x0={186} />

      {/* ANTES / DESPUÉS labels — below scenes */}
      <g>
        <rect x="22" y="144" width="56" height="14" rx="2" fill="rgba(245,247,250,0.08)" stroke="rgba(245,247,250,0.45)" strokeWidth="1" />
        <text x="50" y="154" fontSize="8" textAnchor="middle" fill="rgba(245,247,250,0.7)" fontWeight="800" fontFamily="Inter, sans-serif" letterSpacing="0.5">ANTES</text>
      </g>
      <g>
        <rect x="202" y="144" width="64" height="14" rx="2" fill="rgba(255,200,87,0.18)" stroke="#FFC857" strokeWidth="1" />
        <text x="234" y="154" fontSize="8" textAnchor="middle" fill="#FFC857" fontWeight="800" fontFamily="Inter, sans-serif" letterSpacing="0.5">DESPUÉS</text>
      </g>
    </svg>
  );
}

export function AutomationIllustration() {
  // ── shared: desk silhouette ───────────────────────────────
  const Desk = ({ x0 }) => {
    const x1 = x0 + 88;
    return (
      <g>
        {/* desk top */}
        <rect x={x0 - 2} y="115" width={x1 - x0 + 4} height="3" fill="rgba(255,200,87,0.7)" />
        {/* desk legs */}
        <rect x={x0 + 6} y="118" width="2.4" height="17" fill="rgba(255,200,87,0.5)" />
        <rect x={x1 - 8} y="118" width="2.4" height="17" fill="rgba(255,200,87,0.5)" />
        {/* floor */}
        <line x1={x0 - 2} y1="135" x2={x1 + 2} y2="135" stroke="rgba(245,247,250,0.25)" strokeWidth="0.8" />
      </g>
    );
  };

  // ── LEFT (chaos, demand for resources) ────────────────────
  const ChaosScene = ({ x0 }) => {
    return (
      <g>
        <Desk x0={x0} />

        {/* MONITOR 1 (sitting on the desk, left side) — chaotic spreadsheet */}
        <g transform={`translate(${x0 + 4}, 84)`}>
          <rect x="0" y="0" width="34" height="26" rx="1.2" fill="rgba(11,31,58,0.92)" stroke="rgba(245,247,250,0.55)" strokeWidth="0.8" />
          {/* spreadsheet grid */}
          {[5, 9, 13, 17, 21].map((y, i) => (
            <line key={i} x1="2" y1={y} x2="32" y2={y} stroke="rgba(245,247,250,0.22)" strokeWidth="0.4" />
          ))}
          {[9, 16, 23, 30].map((x, i) => (
            <line key={i} x1={x} y1="2" x2={x} y2="24" stroke="rgba(245,247,250,0.22)" strokeWidth="0.4" />
          ))}
          {/* random red error cells */}
          <rect x="9" y="9" width="7" height="4" fill="rgba(239,68,68,0.6)" />
          <rect x="23" y="17" width="7" height="4" fill="rgba(239,68,68,0.6)" />
          <rect x="16" y="5" width="7" height="4" fill="rgba(255,200,87,0.45)" />
          {/* monitor base — sits on desk */}
          <rect x="14" y="26" width="6" height="3" fill="rgba(245,247,250,0.45)" />
          <rect x="10" y="29" width="14" height="2" fill="rgba(245,247,250,0.55)" />
        </g>

        {/* MONITOR 2 (on the desk, right side) — even more chaotic */}
        <g transform={`translate(${x0 + 48}, 88)`}>
          <rect x="0" y="0" width="36" height="22" rx="1.2" fill="rgba(11,31,58,0.92)" stroke="rgba(245,247,250,0.55)" strokeWidth="0.8" />
          {/* spreadsheet grid */}
          {[5, 9, 13, 17].map((y, i) => (
            <line key={i} x1="2" y1={y} x2="34" y2={y} stroke="rgba(245,247,250,0.22)" strokeWidth="0.4" />
          ))}
          {[10, 18, 26].map((x, i) => (
            <line key={i} x1={x} y1="2" x2={x} y2="20" stroke="rgba(245,247,250,0.22)" strokeWidth="0.4" />
          ))}
          <rect x="2" y="9" width="8" height="4" fill="rgba(239,68,68,0.6)" />
          <rect x="18" y="13" width="8" height="4" fill="rgba(239,68,68,0.6)" />
          <rect x="26" y="5" width="8" height="4" fill="rgba(239,68,68,0.45)" />
          <rect x="10" y="5" width="8" height="4" fill="rgba(255,200,87,0.5)" />
          {/* red alert dot */}
          <circle cx="32" cy="3" r="2" fill="#EF4444" />
          {/* monitor base */}
          <rect x="15" y="22" width="6" height="3" fill="rgba(245,247,250,0.45)" />
          <rect x="11" y="25" width="14" height="2" fill="rgba(245,247,250,0.55)" />
        </g>

        {/* papers flying around the chaos */}
        <g>
          {/* top-left paper */}
          <g transform={`rotate(-22 ${x0 + 8} 42)`}>
            <rect x={x0 + 2} y="38" width="12" height="9" fill="rgba(245,247,250,0.9)" />
            <line x1={x0 + 4} y1="41" x2={x0 + 12} y2="41" stroke="rgba(11,31,58,0.45)" strokeWidth="0.4" />
            <line x1={x0 + 4} y1="43" x2={x0 + 11} y2="43" stroke="rgba(11,31,58,0.45)" strokeWidth="0.4" />
            <line x1={x0 + 4} y1="45" x2={x0 + 10} y2="45" stroke="rgba(11,31,58,0.45)" strokeWidth="0.4" />
          </g>
          {/* top-right paper */}
          <g transform={`rotate(18 ${x0 + 72} 44)`}>
            <rect x={x0 + 66} y="40" width="12" height="9" fill="rgba(245,247,250,0.9)" />
            <line x1={x0 + 68} y1="43" x2={x0 + 76} y2="43" stroke="rgba(11,31,58,0.45)" strokeWidth="0.4" />
            <line x1={x0 + 68} y1="45" x2={x0 + 75} y2="45" stroke="rgba(11,31,58,0.45)" strokeWidth="0.4" />
            <line x1={x0 + 68} y1="47" x2={x0 + 74} y2="47" stroke="rgba(11,31,58,0.45)" strokeWidth="0.4" />
          </g>
          {/* mid-left paper */}
          <g transform={`rotate(-12 ${x0 + 10} 60)`}>
            <rect x={x0 + 4} y="56" width="11" height="8" fill="rgba(245,247,250,0.85)" />
            <line x1={x0 + 6} y1="59" x2={x0 + 13} y2="59" stroke="rgba(11,31,58,0.45)" strokeWidth="0.4" />
            <line x1={x0 + 6} y1="61" x2={x0 + 12} y2="61" stroke="rgba(11,31,58,0.45)" strokeWidth="0.4" />
          </g>
          {/* mid-right paper */}
          <g transform={`rotate(28 ${x0 + 80} 64)`}>
            <rect x={x0 + 74} y="60" width="12" height="9" fill="rgba(245,247,250,0.85)" />
            <line x1={x0 + 76} y1="63" x2={x0 + 84} y2="63" stroke="rgba(11,31,58,0.45)" strokeWidth="0.4" />
            <line x1={x0 + 76} y1="65" x2={x0 + 83} y2="65" stroke="rgba(11,31,58,0.45)" strokeWidth="0.4" />
            <line x1={x0 + 76} y1="67" x2={x0 + 82} y2="67" stroke="rgba(11,31,58,0.45)" strokeWidth="0.4" />
          </g>
          {/* center top paper */}
          <g transform={`rotate(8 ${x0 + 42} 32)`}>
            <rect x={x0 + 36} y="28" width="12" height="9" fill="rgba(245,247,250,0.9)" />
            <line x1={x0 + 38} y1="31" x2={x0 + 46} y2="31" stroke="rgba(11,31,58,0.45)" strokeWidth="0.4" />
            <line x1={x0 + 38} y1="33" x2={x0 + 45} y2="33" stroke="rgba(11,31,58,0.45)" strokeWidth="0.4" />
          </g>
        </g>

        {/* sticky notes stuck to desk-front */}
        <g>
          <rect x={x0 + 38} y="119" width="10" height="10" fill="#FFE066" stroke="rgba(11,31,58,0.35)" strokeWidth="0.4" transform={`rotate(6 ${x0 + 43} 124)`} />
          <line x1={x0 + 40} y1="123" x2={x0 + 46} y2="123" stroke="rgba(11,31,58,0.55)" strokeWidth="0.4" transform={`rotate(6 ${x0 + 43} 124)`} />
        </g>

        {/* stack of papers on the desk — small pile in front of monitors */}
        <g transform={`translate(${x0 + 40}, 109)`}>
          {[0, 1.4, 2.8, 4.2].map((dy, i) => (
            <rect key={i} x={dy * 0.4} y={dy} width="9" height="1.8" fill="rgba(245,247,250,0.85)" stroke="rgba(11,31,58,0.4)" strokeWidth="0.4" />
          ))}
        </g>

        {/* coffee mug — left of desk */}
        <g transform={`translate(${x0 + 4}, 105)`}>
          <rect x="0" y="0" width="8" height="10" rx="1" fill="rgba(245,247,250,0.6)" stroke="rgba(11,31,58,0.6)" strokeWidth="0.5" />
          <path d="M8 2.5 q 2.5 0 2.5 2.5 q 0 2.5 -2.5 2.5" stroke="rgba(11,31,58,0.6)" strokeWidth="0.5" fill="none" />
        </g>

        {/* clock at top right (late hour) */}
        <g transform={`translate(${x0 + 78}, 22)`}>
          <circle cx="0" cy="0" r="7" fill="rgba(11,31,58,0.7)" stroke="rgba(239,68,68,0.85)" strokeWidth="1.2" />
          <line x1="0" y1="0" x2="-3" y2="-2" stroke="#EF4444" strokeWidth="1.2" strokeLinecap="round" />
          <line x1="0" y1="0" x2="0" y2="-5" stroke="#EF4444" strokeWidth="1.2" strokeLinecap="round" />
          <circle cx="0" cy="0" r="0.8" fill="#EF4444" />
        </g>

        {/* stress squiggles above monitors */}
        <g stroke="#EF4444" strokeWidth="1.2" fill="none" strokeLinecap="round">
          <path d={`M ${x0 + 22} 76 q 2 -3 4 0 q 2 3 4 0`} />
          <path d={`M ${x0 + 52} 80 q 2 -3 4 0 q 2 3 4 0`} />
        </g>
      </g>
    );
  };

  // ── RIGHT (automated, neat, calm) ─────────────────────────
  const RelaxedScene = ({ x0 }) => {
    return (
      <g>
        <Desk x0={x0} />

        {/* morning sun top corner */}
        <g transform={`translate(${x0 + 78}, 32)`}>
          <circle cx="0" cy="0" r="5" fill="rgba(255,200,87,0.55)" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => {
            const rad = (deg * Math.PI) / 180;
            const x1 = Math.cos(rad) * 7, y1 = Math.sin(rad) * 7;
            const x2 = Math.cos(rad) * 10, y2 = Math.sin(rad) * 10;
            return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(255,200,87,0.55)" strokeWidth="1" strokeLinecap="round" />;
          })}
        </g>

        {/* SINGLE clean monitor — sitting on desk, centered */}
        <g transform={`translate(${x0 + 18}, 72)`}>
          <rect x="0" y="0" width="54" height="38" rx="2" fill="rgba(11,31,58,0.92)" stroke="rgba(255,200,87,0.8)" strokeWidth="1.1" />
          {/* faro mini-logo top */}
          <text x="5" y="8" fontSize="5.5" fill="#FFC857" fontWeight="800" fontFamily="Inter, sans-serif" letterSpacing="0.5">FARO</text>
          <text x="49" y="8" fontSize="4.5" textAnchor="end" fill="rgba(245,247,250,0.5)" fontWeight="700" fontFamily="Inter, sans-serif">07:00</text>
          {/* sub heading */}
          <text x="27" y="16" fontSize="4.5" textAnchor="middle" fill="rgba(245,247,250,0.9)" fontWeight="700" fontFamily="Inter, sans-serif">ÓRDENES LISTAS</text>
          {/* check rows */}
          {[20, 25, 30].map((y, i) => (
            <g key={i}>
              <circle cx="5" cy={y} r="1.6" fill="#4ade80" />
              <path d={`M 4 ${y} L 4.7 ${y + 0.8} L 6 ${y - 0.8}`} stroke="#0B1F3A" strokeWidth="0.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              <rect x="9" y={y - 1} width="24" height="1.5" fill="rgba(245,247,250,0.32)" />
              <rect x="38" y={y - 1} width="12" height="1.5" fill="rgba(255,200,87,0.75)" />
            </g>
          ))}
          {/* monitor base — sits on desk */}
          <rect x="24" y="38" width="6" height="3" fill="rgba(245,247,250,0.5)" />
          <rect x="20" y="41" width="14" height="2" fill="rgba(245,247,250,0.55)" />
        </g>

        {/* coffee mug on desk, right side */}
        <g transform={`translate(${x0 + 74}, 104)`}>
          <rect x="0" y="0" width="9" height="11" rx="1" fill="#FFFFFF" stroke="rgba(11,31,58,0.6)" strokeWidth="0.5" />
          <path d="M9 3 q 3 0 3 3 q 0 3 -3 3" stroke="rgba(11,31,58,0.6)" strokeWidth="0.5" fill="none" />
          {/* steam */}
          <path d="M2 -3 q -1 -2 1 -4" stroke="rgba(245,247,250,0.55)" strokeWidth="0.6" fill="none" strokeLinecap="round" />
          <path d="M5 -3 q 1 -2 -1 -4" stroke="rgba(245,247,250,0.55)" strokeWidth="0.6" fill="none" strokeLinecap="round" />
        </g>

        {/* big green CHECK badge top-left */}
        <g transform={`translate(${x0 + 8}, 56)`}>
          <circle cx="0" cy="0" r="7" fill="rgba(74,222,128,0.18)" stroke="#4ade80" strokeWidth="1.2" />
          <path d="M -3 0 L -0.8 2.2 L 3 -2" stroke="#4ade80" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </g>
      </g>
    );
  };

  return (
    <svg viewBox="0 0 280 160" className="w-full h-full">
      {/* dark backdrop */}
      <rect x="0" y="0" width="280" height="160" fill="#0B1F3A" />
      <rect x="0" y="155" width="280" height="5" fill="#06122A" />

      {/* LEFT — manual chaos */}
      <ChaosScene x0={6} />

      {/* CENTER — FARO logo */}
      <g>
        <circle cx="140" cy="80" r="26" fill="rgba(255,200,87,0.10)" />
        <image href={faroLogo} x="112" y="58" width="56" height="44" preserveAspectRatio="xMidYMid meet" />
      </g>

      {/* RIGHT — automated calm */}
      <RelaxedScene x0={186} />

      {/* ANTES / DESPUÉS labels — below scenes */}
      <g>
        <rect x="22" y="144" width="56" height="14" rx="2" fill="rgba(245,247,250,0.08)" stroke="rgba(245,247,250,0.45)" strokeWidth="1" />
        <text x="50" y="154" fontSize="8" textAnchor="middle" fill="rgba(245,247,250,0.7)" fontWeight="800" fontFamily="Inter, sans-serif" letterSpacing="0.5">ANTES</text>
      </g>
      <g>
        <rect x="202" y="144" width="64" height="14" rx="2" fill="rgba(255,200,87,0.18)" stroke="#FFC857" strokeWidth="1" />
        <text x="234" y="154" fontSize="8" textAnchor="middle" fill="#FFC857" fontWeight="800" fontFamily="Inter, sans-serif" letterSpacing="0.5">DESPUÉS</text>
      </g>
    </svg>
  );
}

/* ─── Why-it-matters Card ─── */
/* Small version of the stock chart for each benefit: the bands close on the optimal point.
   `focus` lights the band the benefit is about. */
function MiniStockChart({ en, focus }) {
  const on = (band) => focus === "both" || focus === band;
  return (
    <svg viewBox="0 0 500 202" className="w-full max-w-[440px] h-auto mt-5" aria-hidden="true">
      <line x1="34" y1="20" x2="34" y2="178" stroke={C.navy} strokeWidth="1.5" />
      <line x1="34" y1="178" x2="384" y2="178" stroke={C.navy} strokeWidth="1.5" />
      <path d="M34 12 l-5 9 h10 z" fill={C.navy} />
      <path d="M392 178 l-9 -5 v10 z" fill={C.navy} />
      <text x="44" y="18" style={{ fontSize: 12, fontWeight: 700, fill: C.navy, fontFamily: SANS }}>Stock</text>
      <text x="210" y="198" style={{ fontSize: 12, fontWeight: 700, fill: C.navy, textAnchor: "middle", fontFamily: SANS }}>{en ? "Time with RUMBO" : "Tiempo con RUMBO"}</text>
      <g transform="translate(24 28)">
      <path d="M10 10 C 120 14, 230 66, 330 72 L 330 75 L 10 75 Z" fill={C.amber} opacity={on("over") ? 0.45 : 0.12} />
      <path d="M10 140 C 120 136, 230 84, 330 78 L 330 75 L 10 75 Z" fill={C.grayLight} opacity={on("out") ? 0.5 : 0.14} />
      <path d="M10 10 C 120 14, 230 66, 330 72" fill="none" stroke={C.amberDark} strokeWidth="2.5" opacity={on("over") ? 1 : 0.3} />
      <path d="M10 140 C 120 136, 230 84, 330 78" fill="none" stroke={C.gray} strokeWidth="2.5" opacity={on("out") ? 1 : 0.3} />
      <line x1="10" y1="75" x2="330" y2="75" stroke={C.navy} strokeWidth="2" />
      {on("over") && <text x="22" y="52" style={{ fontSize: 13, fontWeight: 700, fill: C.navy, fontFamily: SANS }}>{en ? "Overstock" : "Sobrestock"}</text>}
      {on("out") && <text x="22" y="108" style={{ fontSize: 13, fontWeight: 700, fill: C.navy, fontFamily: SANS }}>{en ? "Stockouts" : "Quiebres de stock"}</text>}
      <line x1="330" y1="75" x2="330" y2="150" stroke={C.navy} strokeWidth="1.5" strokeDasharray="4 4" />
      <circle cx="330" cy="75" r="13" fill={C.amber} opacity=".3" />
      <circle cx="330" cy="75" r="6.5" fill={C.amber} stroke={C.navy} strokeWidth="2" />
      <rect x="348" y="61" width="118" height="28" rx="14" fill={C.navy} />
      <text x="407" y="80" style={{ fontSize: 13, fontWeight: 700, fill: C.amber, textAnchor: "middle", fontFamily: SANS }}>{en ? "Optimal stock" : "Stock óptimo"}</text>
      </g>
    </svg>
  );
}

function WhyCard({ illustration, title, text, index, impact, en }) {
  const Illustration =
    illustration === "stockout" ? StockoutIllustration :
    illustration === "overstock" ? OverstockIllustration :
    AutomationIllustration;
  return (
    <motion.div custom={index} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
      className="relative rounded-2xl p-6 md:p-8 grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-6 md:gap-10 items-center"
      style={{
        background: C.paper,
        border: "1px solid rgba(11,31,58,0.08)",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 12px 30px rgba(11,31,58,0.18)",
      }}>
      <div className="rounded-xl overflow-hidden aspect-[7/4] flex items-center justify-center p-2 md:p-3"
        style={{ background: C.navy, border: "1px solid rgba(11,31,58,0.15)" }}>
        <Illustration />
      </div>
      <div>
        <h4 className="text-[20px] md:text-[24px] font-bold leading-[1.25] mb-3" style={{ color: C.navy, letterSpacing: "-0.01em" }}>
          {title}
          {impact && (
            <>
              {" "}
              <span style={{ color: C.amberDark }}>→</span>{" "}
              <span style={{ color: C.amberDark, fontFamily: SERIF, fontStyle: "italic", fontWeight: 700 }}>{impact}</span>
            </>
          )}
        </h4>
        <p className="text-[14px] md:text-[16px] leading-[1.6]" style={{ color: C.grayDark }}>
          {text}
        </p>
        <MiniStockChart en={en} focus={illustration === "stockout" ? "out" : illustration === "overstock" ? "over" : "both"} />
      </div>
    </motion.div>
  );
}

/* Overstock and stockouts both lose money; RUMBO narrows both to the optimal level */
function StockFunnelChart({ en }) {
  const t = en ? {
    title: "RUMBO takes your stock to its optimal level.",
    lead: <>RUMBO generates the replenishment orders for each product in each store with the <b>optimal quantity</b>, calculated from an <b>accurate demand forecast</b>. So at the end of each replenishment period, the stock of each product in each store sits at its optimal level: no stockouts and no overstock.</>,
    aria: "Overstock and stockouts both lose money; with RUMBO both narrow to the optimal stock",
    optimal: "Optimal stock", over: "Overstock", out: "Stockouts",
    lost: ["Money", "lost"], stock: "Stock", time: "Time with RUMBO",
  } : {
    title: "RUMBO lleva tu stock a su nivel óptimo.",
    lead: <>RUMBO genera las órdenes de reposición de cada producto en cada tienda con la <b>cantidad óptima</b>, calculada a partir de una <b>predicción precisa de la demanda</b>. Así, al final de cada período de reposición, el stock de cada producto en cada tienda queda en su nivel óptimo: sin quiebres y sin sobrestock.</>,
    aria: "El sobrestock y los quiebres de stock hacen perder dinero; con RUMBO ambos se reducen hasta el stock óptimo",
    optimal: "Stock óptimo", over: "Sobrestock", out: "Quiebres de stock",
    lost: ["Pérdida", "de dinero"], stock: "Stock", time: "Tiempo con RUMBO",
  };
  const band = { fontSize: 16, fontWeight: 700, fill: C.navy, fontFamily: SANS };
  const lost = (fill) => ({ fontSize: 17, fontWeight: 700, fill, textAnchor: "end", fontFamily: SANS });
  return (
    <motion.div custom={3} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
      className="rounded-2xl p-6 md:p-10"
      style={{ background: C.paper, border: "1px solid rgba(255,200,87,0.4)", boxShadow: "0 20px 60px rgba(0,0,0,0.35)" }}>
      <h3 className="text-center text-[18px] md:text-[22px] font-bold leading-[1.35] max-w-2xl mx-auto mb-6"
        style={{ color: C.navy, fontFamily: SANS, letterSpacing: "-0.01em" }}>
        {t.title}
      </h3>
      <p className="text-center text-[15px] md:text-[17px] leading-[1.7] max-w-4xl mx-auto -mt-2 mb-8 [&_b]:font-bold [&_b]:text-[#0B1F3A]" style={{ color: C.grayDark, fontFamily: SANS }}>
        {t.lead}
      </p>
      <svg viewBox="0 0 740 380" role="img" aria-label={t.aria} className="w-full h-auto max-w-[740px] mx-auto block">
        <defs>
          <marker id="funnel-arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0 0 L10 5 L0 10 z" fill={C.navy} />
          </marker>
        </defs>
        <path d="M180 40 C 310 50, 440 150, 580 163 L 580 185 L 180 185 Z" fill={C.amber} opacity=".38" />
        <path d="M180 330 C 310 320, 440 220, 580 207 L 580 185 L 180 185 Z" fill={C.grayLight} opacity=".38" />
        <path d="M180 40 C 310 50, 440 150, 580 163" fill="none" stroke={C.amberDark} strokeWidth="3" />
        <path d="M180 330 C 310 320, 440 220, 580 207" fill="none" stroke={C.gray} strokeWidth="3" />
        <line x1="180" y1="185" x2="580" y2="185" stroke={C.navy} strokeWidth="2.5" />
        <circle cx="580" cy="185" r="16" fill={C.amber} opacity=".3" />
        <circle cx="580" cy="185" r="8" fill={C.amber} stroke={C.navy} strokeWidth="2.5" />
        <rect x="602" y="169" width="132" height="32" rx="16" fill={C.navy} />
        <text x="668" y="190.5" style={{ ...band, fontSize: 15, fill: C.amber, textAnchor: "middle" }}>{t.optimal}</text>
        <text x="200" y="150" style={band}>{t.over}</text>
        <text x="200" y="232" style={band}>{t.out}</text>

        <line x1="150" y1="42" x2="150" y2="183" stroke={C.navy} strokeWidth="1.5" markerStart="url(#funnel-arrow)" markerEnd="url(#funnel-arrow)" />
        <text x="136" y="108" style={lost(C.amberDark)}><tspan x="136">{t.lost[0]}</tspan><tspan x="136" dy="21">{t.lost[1]}</tspan></text>
        <line x1="150" y1="187" x2="150" y2="328" stroke={C.navy} strokeWidth="1.5" markerStart="url(#funnel-arrow)" markerEnd="url(#funnel-arrow)" />
        <text x="136" y="252" style={lost(C.gray)}><tspan x="136">{t.lost[0]}</tspan><tspan x="136" dy="21">{t.lost[1]}</tspan></text>

        <line x1="180" y1="25" x2="180" y2="345" stroke={C.navy} strokeWidth="1.5" />
        <line x1="180" y1="345" x2="600" y2="345" stroke={C.navy} strokeWidth="1.5" />
        <path d="M180 16 l-6 10 h12 z" fill={C.navy} />
        <text x="192" y="24" style={{ ...band, fontSize: 13 }}>{t.stock}</text>
        <path d="M608 345 l-10 -6 v12 z" fill={C.navy} />
        <text x="390" y="372" style={{ ...band, fontSize: 14, textAnchor: "middle" }}>{t.time}</text>
      </svg>
    </motion.div>
  );
}

/* Pilot offer (deck slide 11): demo → set-up → free month → what follows */
function PilotTimeline({ en }) {
  const t = en ? [
    { k: "Demo", h: "Get to know the platform", items: ["A walk through RUMBO running", "How it fits your operation", "We answer your questions"] },
    { k: "Set-up", h: "Sign-up and connection", items: ["Your account on the platform", "Connection to your system", "Models trained on your data"] },
    { k: "Free month · 30 days", h: "RUMBO in your operation", blocks: [
      { h: "Every day's orders", p: "You get the orders RUMBO generates, review the forecasts and use every feature." },
      { h: "Survey of your losses", p: "How much you lost last month to stockouts and overstock." },
    ], free: true },
    { k: "If you decide to continue", h: "Your loss starts going down, month after month", chart: true,
      p: "Stockouts and overstock keep shrinking, closer and closer to the optimal stock." },
  ] : [
    { k: "Demo", h: "Conocé la plataforma", items: ["Recorrido por RUMBO funcionando", "Cómo se adapta a tu operación", "Respondemos tus dudas"] },
    { k: "Puesta en marcha", h: "Alta y conexión", items: ["Alta de tu cuenta en la plataforma", "Conexión con tu sistema", "Entrenamiento de los modelos con tus datos"] },
    { k: "Mes gratis · 30 días", h: "RUMBO en tu operación", blocks: [
      { h: "Órdenes de cada día", p: "Recibís las órdenes que genera RUMBO, revisás las predicciones y usás todas las funciones." },
      { h: "Relevamiento de tus pérdidas", p: "Cuánto perdiste el último mes por quiebre y sobrestock." },
    ], free: true },
    { k: "Si decidís seguir", h: "Tu pérdida empieza a bajar, mes a mes", chart: true,
      p: "Quiebres y sobrestock se siguen reduciendo, cada vez más cerca del stock óptimo." },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1.35fr_1fr] gap-4 md:gap-5 text-left">
      {t.map((ph, i) => (
        <motion.div key={ph.k} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="relative rounded-xl p-5 md:p-6 flex flex-col"
          style={{
            background: ph.free ? "rgba(255,200,87,0.10)" : "rgba(255,255,255,0.04)",
            border: `1px solid ${ph.free ? "rgba(255,200,87,0.55)" : "rgba(255,255,255,0.10)"}`,
          }}>
          <div className="flex items-center gap-2.5 mb-3">
            <span className="w-7 h-7 rounded-full flex items-center justify-center text-[13px] font-bold flex-shrink-0"
              style={{ background: ph.free ? C.amber : "transparent", color: ph.free ? C.navy : C.amber, border: `1.5px solid ${C.amber}`, fontFamily: SANS }}>
              {i}
            </span>
            <span className="text-[10.5px] font-bold uppercase tracking-[0.2em]" style={{ color: C.amber, fontFamily: MONO }}>{ph.k}</span>
          </div>
          <h3 className="text-[18px] md:text-[20px] font-bold leading-[1.25] mb-3" style={{ color: C.white, fontFamily: SANS }}>{ph.h}</h3>
          {ph.items && (
            <ul className="flex flex-col gap-2">
              {ph.items.map((it) => (
                <li key={it} className="flex gap-2 text-[14px] leading-[1.5]" style={{ color: C.grayLight, fontFamily: SANS }}>
                  <span style={{ color: C.amber }}>✓</span>{it}
                </li>
              ))}
            </ul>
          )}
          {ph.blocks && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {ph.blocks.map((b) => (
                <div key={b.h}>
                  <h4 className="text-[14.5px] font-bold mb-1" style={{ color: C.white, fontFamily: SANS }}>{b.h}</h4>
                  <p className="text-[13.5px] leading-[1.55]" style={{ color: C.grayLight, fontFamily: SANS }}>{b.p}</p>
                </div>
              ))}
            </div>
          )}
          {ph.chart && (
            <>
              <svg viewBox="0 0 230 120" className="w-full h-auto my-1" aria-hidden="true">
                <path d="M10 10 C 60 14, 110 46, 170 54 L 170 60 L 10 60 Z" fill={C.amber} opacity=".4" />
                <path d="M10 110 C 60 106, 110 74, 170 66 L 170 60 L 10 60 Z" fill={C.grayLight} opacity=".35" />
                <path d="M10 10 C 60 14, 110 46, 170 54" fill="none" stroke={C.amber} strokeWidth="2.2" />
                <path d="M10 110 C 60 106, 110 74, 170 66" fill="none" stroke={C.grayLight} strokeWidth="2.2" />
                <path d="M170 54 C 190 57, 208 59, 224 59.5" fill="none" stroke={C.amber} strokeWidth="2.2" strokeDasharray="5 4" opacity=".6" />
                <path d="M170 66 C 190 63, 208 61, 224 60.5" fill="none" stroke={C.grayLight} strokeWidth="2.2" strokeDasharray="5 4" opacity=".6" />
                <line x1="10" y1="60" x2="226" y2="60" stroke={C.white} strokeWidth="1.6" />
                <text x="224" y="78" style={{ fontSize: 9, fill: C.grayLight, textAnchor: "end", fontFamily: SANS }}>{en ? "optimal stock" : "stock óptimo"}</text>
              </svg>
              <p className="text-[13.5px] leading-[1.55]" style={{ color: C.grayLight, fontFamily: SANS }}>{ph.p}</p>
            </>
          )}
        </motion.div>
      ))}
    </div>
  );
}

/* ═══ MAIN PAGE ═══ */
export default function RumboPage({ language, onNavigate }) {
  const en = language === "en";

  const stats = en ? [
    { icon: "orders", k: "Automated orders" },
    { icon: "forecast", k: "Accurate demand forecasting" },
    { icon: "radar", k: "Order tracking and insights" },
    { icon: "losses", k: "Stockout and overstock tracking" },
  ] : [
    { icon: "orders", k: "Órdenes automatizadas" },
    { icon: "forecast", k: "Predicción precisa de demanda" },
    { icon: "radar", k: "Seguimiento de pedidos e insights" },
    { icon: "losses", k: "Seguimiento de quiebres y sobrestock" },
  ];

  const benefits = en ? [
    {
      illustration: "stockout",
      title: "Fewer stockouts",
      impact: "More sales",
      text: "Shelves stocked every day. Every possible sale gets captured — and every customer finds what they came for.",
    },
    {
      illustration: "overstock",
      title: "Less overstock",
      impact: "Fewer losses",
      text: "Stock sized to actual demand. No more losses from expirations and obsolescence, capital freed up and a warehouse that's always moving.",
    },
    {
      illustration: "automation",
      title: "Automated, daily replenishment",
      impact: "Optimal decisions",
      text: "Thousands of replenishment decisions taken intelligently every day, with full control and a clear view of the whole stock management from one place.",
    },
  ] : [
    {
      illustration: "stockout",
      title: "Menos rupturas de stock",
      impact: "Más ventas",
      text: "Góndolas abastecidas todos los días. Cada venta posible queda captada — y cada cliente encuentra lo que vino a buscar.",
    },
    {
      illustration: "overstock",
      title: "Menos sobrestock",
      impact: "Menos pérdidas",
      text: "Stock dimensionado a la demanda real. Sin pérdidas por vencimientos y obsolescencia, con el capital liberado y un depósito siempre en movimiento.",
    },
    {
      illustration: "automation",
      title: "Reposición diaria automatizada",
      impact: "Decisiones óptimas",
      text: "Miles de decisiones de reposición tomadas inteligentemente todos los días, con control integral y visibilidad de toda la gestión de stock desde un solo lugar.",
    },
  ];

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=DM+Sans:wght@400;500;600;700&display=swap');`}</style>

      {/* ═══ 1. HERO — full viewport, text + logo only ═══ */}
      <section className="relative min-h-screen flex items-center overflow-hidden py-16"
        style={{ background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 100%)` }}>
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: C.amber }} />
        <AmberSidebar />
        <FaroWatermark className="top-0 right-0 w-[60%] h-full" position="right" />
        <Container className="relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* LEFT — text */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="text-center lg:text-left">
              <h1 className="text-[56px] md:text-[80px] lg:text-[96px] font-bold leading-[1]"
                style={{ color: C.white, fontFamily: SERIF, letterSpacing: "0.02em" }}>
                RUMBO
              </h1>
              <p className="mt-3 text-[16px] md:text-[20px] lg:text-[22px] italic leading-[1.3]"
                style={{ color: C.amber, fontFamily: SERIF, fontWeight: 500 }}>
                {en
                  ? "the optimal trajectory of your merchandise"
                  : "la reposición óptima de tu mercadería"}
              </p>
              <AmberRule width="120px" className="mt-5 mx-auto lg:mx-0" />
              <p className="mt-6 text-[17px] md:text-[19px] lg:text-[20px] leading-[1.55] max-w-xl mx-auto lg:mx-0"
                style={{ color: C.white, opacity: 0.92 }}>
                {en
                  ? <>RUMBO automates the calculation of the{" "}
                      <span style={{ color: C.white, fontWeight: 600 }}>optimal quantity</span> to replenish for every product in every store.<br />Daily replenishment decisions, ready to execute.</>
                  : <>RUMBO automatiza el cálculo de la{" "}
                      <span style={{ color: C.white, fontWeight: 600 }}>cantidad óptima</span>{" "}a reponer de cada producto en cada tienda.<br />Decisiones de reposición diarias, listas para ejecutar.</>}
              </p>
              <p className="mt-5 text-[18px] md:text-[20px] lg:text-[22px] font-bold leading-[1.3] max-w-xl mx-auto lg:mx-0"
                style={{ color: C.amber, fontFamily: SANS, letterSpacing: "-0.01em" }}>
                {en
                  ? "Fewer stockouts. Less overstock. More margin."
                  : "Menos quiebres. Menos sobrestock. Más margen."}
              </p>

              <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-3">
                <button
                  onClick={() => onNavigate("contact")}
                  className="rounded-xl text-sm md:text-base px-7 py-3.5 font-bold transition-all hover:-translate-y-0.5"
                  style={{ background: C.amber, color: C.navy, boxShadow: "0 4px 24px rgba(255,200,87,0.35)", fontFamily: SANS }}>
                  {en ? "Get a demo →" : "Pedí una demo →"}
                </button>
                <a
                  href={`${process.env.PUBLIC_URL}/FARO_Rumbo.pdf`}
                  download="FARO_Rumbo.pdf"
                  className="rounded-xl text-sm md:text-base px-7 py-3.5 font-bold transition-all hover:-translate-y-0.5"
                  style={{ border: `1px solid ${C.amber}`, color: C.amber, fontFamily: SANS }}>
                  {en ? "Download brochure" : "Descargar brochure"}
                </a>
              </div>
            </motion.div>

            {/* RIGHT — FARO logo with ambient halo + research/data flavor */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
              className="relative flex items-center justify-center aspect-square w-full max-w-[520px] mx-auto">
              {/* outer halo */}
              <div className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                  background: "radial-gradient(circle, rgba(255,200,87,0.22) 0%, rgba(255,200,87,0.08) 35%, transparent 65%)",
                  filter: "blur(28px)",
                }} />
              {/* inner glow */}
              <div className="absolute pointer-events-none rounded-full"
                style={{
                  width: "55%", height: "55%",
                  background: "radial-gradient(circle, rgba(255,220,140,0.55) 0%, rgba(255,200,87,0.18) 45%, transparent 70%)",
                  filter: "blur(20px)",
                }} />

              {/* ambient research / data science layer */}
              <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full pointer-events-none" style={{ opacity: 0.55 }}>
                {/* slow rotating orbit 1 — dashed circle with data nodes */}
                <motion.g
                  animate={{ rotate: 360 }}
                  transition={{ duration: 32, repeat: Infinity, ease: "linear" }}
                  style={{ transformOrigin: "200px 200px" }}>
                  <circle cx="200" cy="200" r="170" fill="none" stroke="rgba(255,200,87,0.18)" strokeWidth="0.8" strokeDasharray="2 6" />
                  <circle cx="200" cy="30" r="3" fill="#FFC857" opacity="0.7" />
                  <circle cx="370" cy="200" r="2" fill="#FFC857" opacity="0.5" />
                  <circle cx="200" cy="370" r="2.5" fill="#FFC857" opacity="0.6" />
                </motion.g>

                {/* slow rotating orbit 2 — counter direction */}
                <motion.g
                  animate={{ rotate: -360 }}
                  transition={{ duration: 48, repeat: Infinity, ease: "linear" }}
                  style={{ transformOrigin: "200px 200px" }}>
                  <circle cx="200" cy="200" r="140" fill="none" stroke="rgba(255,200,87,0.12)" strokeWidth="0.6" strokeDasharray="1 4" />
                  <circle cx="60" cy="200" r="2.5" fill="#FFC857" opacity="0.55" />
                  <circle cx="200" cy="60" r="2" fill="#FFC857" opacity="0.4" />
                </motion.g>

              </svg>

              {/* logo on top */}
              <motion.div
                animate={{ opacity: [0.92, 1, 0.92] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="relative z-10"
                style={{ transform: "scaleX(-1)" }}>
                <FaroLogo className="w-[72%] max-w-[340px] h-auto mx-auto" />
              </motion.div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* ═══ 1B. STATS — appears on scroll ═══ */}
      <section className="relative py-10 md:py-14 overflow-hidden"
        style={{ background: C.navyMid }}>
        <AmberSidebar />
        <Container className="relative z-10">
          <div className="max-w-5xl mx-auto">
            <StockFunnelChart en={en} />
          </div>
        </Container>
      </section>

      {/* ═══ 2. KEY FEATURES (interactive accordion, paper) ═══ */}
      <section className="relative py-20 md:py-24 overflow-hidden" style={{ background: C.paper }}>
        <div className="absolute inset-0 pointer-events-none opacity-[0.35]" style={{
          backgroundImage: `radial-gradient(rgba(11,31,58,0.04) 1px, transparent 1px)`,
          backgroundSize: "3px 3px",
        }} />
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: C.amber, opacity: 0.6 }} />
        <Container className="relative z-10">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <span className="inline-block text-[11px] md:text-[12px] font-bold uppercase tracking-[0.32em] mb-4"
              style={{ color: C.amberDark, fontFamily: MONO }}>
              {en ? "Explore the product" : "Explorá el producto"}
            </span>
            <h2 className="text-3xl md:text-[44px] font-extrabold leading-[1.1]"
              style={{ color: C.navy, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.015em" }}>
                {en
                  ? <>See what{" "}<span style={{ color: C.amberDark, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>RUMBO</span>{" "}does for you</>
                  : <>Lo que{" "}<span style={{ color: C.amberDark, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>RUMBO</span>{" "}hace por vos</>}
            </h2>
            <AmberRule width="52px" className="mt-5 mx-auto" />
          </div>

          <div className="max-w-6xl mx-auto">
            <KeyFeatures en={en} />
          </div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="max-w-5xl mx-auto mt-16 rounded-2xl px-6 py-8 md:px-10"
            style={{ background: C.navyMid, border: "1px solid rgba(255,200,87,0.4)", boxShadow: "0 20px 60px rgba(11,31,58,0.25)" }}>
            <h2 className="text-center text-[20px] md:text-[24px] font-bold leading-[1.25] mb-6"
              style={{ color: C.white, fontFamily: SANS, letterSpacing: "-0.015em" }}>
              {en ? "Improve replenishment efficiency" : "Mejorá la eficiencia de reposición"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
              {stats.map((s, i) => (
                <CircularStat key={s.k} icon={s.icon} label={s.k} index={i} size={140} />
              ))}
            </div>
          </motion.div>

        </Container>
        <div className="absolute bottom-0 left-0 w-full h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${C.amber}, transparent)` }} />
        <div className="absolute bottom-0 left-0 w-full h-1" style={{ background: C.amber, opacity: 0.5 }} />
      </section>

      {/* ═══ 3. WHY IT MATTERS (navy bg + light cards) ═══ */}
      <section className="relative py-20 md:py-24 overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${C.navyMid} 0%, ${C.navy} 100%)` }}>
        <AmberSidebar />
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: C.amber, opacity: 0.6 }} />
        <Container className="relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14 max-w-3xl mx-auto">
              <FaroLogo className="h-10 w-auto mx-auto mb-4" />
              <span className="inline-block text-[11px] md:text-[12px] font-bold uppercase tracking-[0.32em] mb-4"
                style={{ color: C.amber, fontFamily: MONO }}>
                {en ? "Key benefits" : "Beneficios clave"}
              </span>
              <h2 className="text-3xl md:text-[44px] font-extrabold leading-[1.1]"
                style={{ color: C.white, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.015em" }}>
                {en
                  ? <>More sales, fewer losses,{" "}<span style={{ color: C.amber, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>optimal decisions</span>{" "}— every day</>
                  : <>Más ventas, menos pérdidas,{" "}<span style={{ color: C.amber, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>decisiones óptimas</span>{" "}— todos los días</>}
              </h2>
              <AmberRule width="52px" className="mt-5 mx-auto" />
            </div>
            <div className="flex flex-col gap-6 md:gap-8">
              {benefits.map((b, i) => (
                <WhyCard key={i} illustration={b.illustration} title={b.title} text={b.text} impact={b.impact} index={i} en={en} />
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ═══ 5. FINAL CTA (paper bg + navy card) ═══ */}
      <section className="relative py-16 md:py-20 overflow-hidden" style={{ background: C.paper }}>
        <div className="absolute inset-0 pointer-events-none opacity-[0.35]" style={{
          backgroundImage: `radial-gradient(rgba(11,31,58,0.04) 1px, transparent 1px)`,
          backgroundSize: "3px 3px",
        }} />
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: C.amber, opacity: 0.6 }} />
        <Container className="relative z-10">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="max-w-6xl mx-auto rounded-2xl p-6 md:p-10 text-center"
            style={{
              background: `linear-gradient(135deg, ${C.navyLight} 0%, ${C.navy} 100%)`,
              border: `1px solid rgba(255,200,87,0.25)`,
              boxShadow: `0 20px 60px rgba(0,0,0,0.35), 0 0 80px rgba(255,200,87,0.08)`,
            }}>
            <span className="inline-block text-[11px] md:text-[12px] font-bold uppercase tracking-[0.32em] mb-4"
              style={{ color: C.amber, fontFamily: MONO }}>
              {en ? "Next step" : "Próximo paso"}
            </span>
            <h2 className="text-[26px] md:text-[34px] font-bold leading-[1.2] mb-8"
              style={{ color: C.white, fontFamily: SANS, letterSpacing: "-0.015em" }}>
              {en
                ? <>Try RUMBO{" "}<span style={{ color: C.amber, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>free for a month</span></>
                : <>Probá RUMBO{" "}<span style={{ color: C.amber, fontFamily: SERIF, fontStyle: "italic", fontWeight: 500 }}>un mes gratis</span></>}
            </h2>
            <PilotTimeline en={en} />
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <button
                onClick={() => onNavigate("contact")}
                className="rounded-xl text-sm md:text-base px-7 py-3.5 font-bold transition-all hover:-translate-y-0.5"
                style={{ background: C.amber, color: C.navy, boxShadow: "0 4px 24px rgba(255,200,87,0.35)", fontFamily: SANS }}>
                {en ? "Request a demo →" : "Pedí una demo →"}
              </button>
            </div>
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="max-w-6xl mx-auto mt-6 rounded-2xl p-6 md:p-10 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 md:gap-10 items-center"
            style={{ background: C.white, border: "1px solid rgba(11,31,58,0.08)", boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 10px 30px rgba(11,31,58,0.06)" }}>
            <div className="flex -space-x-4 justify-center">
              {[romanoImg, rivettiImg].map((src) => (
                <img key={src} src={src} alt="" className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover"
                  style={{ border: `3px solid ${C.white}`, boxShadow: "0 4px 14px rgba(11,31,58,0.15)" }} />
              ))}
            </div>
            <div className="text-center md:text-left">
              <span className="text-[11px] font-bold uppercase tracking-[0.22em]" style={{ color: C.amberDark, fontFamily: MONO }}>
                {en ? "Team" : "Equipo"}
              </span>
              <h3 className="mt-2 text-[22px] md:text-[26px] font-bold leading-[1.2]" style={{ color: C.navy, fontFamily: SANS }}>
                {en ? "Behind FARO there is a team that understands your business." : "Detrás de FARO hay un equipo que entiende tu negocio."}
              </h3>
              <p className="mt-3 text-[14px] md:text-[15px] leading-[1.7] max-w-2xl" style={{ color: C.grayDark, fontFamily: SANS }}>
                {en
                  ? "Specialists in artificial intelligence, data science and software development, with real experience across industries."
                  : "Especialistas en inteligencia artificial, ciencia de datos y desarrollo de software, con experiencia real en varias industrias."}
              </p>
            </div>
            <button onClick={() => onNavigate("about")}
              className="justify-self-center rounded-xl text-sm md:text-base px-6 py-3 font-bold transition-all hover:-translate-y-0.5"
              style={{ background: C.navy, color: C.amber, fontFamily: SANS }}>
              {en ? "Meet the team →" : "Conocé al equipo →"}
            </button>
          </motion.div>
        </Container>
      </section>

    </>
  );
}
